import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Box, Typography, TextField, MenuItem, Select, FormControl, InputLabel,
  Switch, FormControlLabel, Slider, Stack, Tooltip, Alert,
  Tabs, Tab, Paper, PaperProps,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import toast from 'react-hot-toast';
import { chartSettingsService } from '../../services/chartSettings.service';
import { useChartSettings } from '../../contexts/ChartSettingsContext';
import { FRACTION_DENOMINATORS, normaliseDenominator } from '../../utils/candleFraction';
import { VPA_SIGNAL_DEFS, VPA_TOGGLE_DEFAULTS, type VpaToggles, type VpaGroup } from '../../utils/vpaIndicators';
import {
  ChartSettingsPayload, ChartSettingsTemplate, resolvePayload, DEFAULT_CHART_SETTINGS,
  SessionLineStyle, resetVisibleBars
} from '../../services/chartSettings.service';

interface Props { open: boolean; onClose: () => void; }

// R115 follow-up — draggable Paper for the Chart Settings dialog. The
// .draggable-handle inside DialogTitle is what the user grabs; everything
// else inside the dialog stays clickable / interactive.
function DraggablePaper(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable nodeRef={nodeRef} handle=".chartsettings-drag-handle" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Uniform control cell — every field (colour / slider / labelled control) uses
// the same fixed width so they tile into clean, aligned columns inside a
// flex-wrapped section body, instead of the ragged widths they used before.
const FIELD_W = 224;
// Each control is a flex cell that grows from a 224px basis, so a wrapped row of
// them fills the width in even, aligned columns (instead of ragged fixed widths).
// `flex` is honoured in the existing flex-wrap section bodies; in a CSS grid the
// item simply stretches to fill its column — so the same cell works in both.
const fieldCellSx = { flex: `1 1 ${FIELD_W}px`, minWidth: 0, boxSizing: 'border-box' as const };

const fieldLabelSx = { display: 'block', mb: 0.25, lineHeight: 1.3, fontWeight: 600 } as const;

/** Tiny native color input wrapped in MUI styling. Keeps the bundle size
 *  smaller than pulling in a heavy picker dependency. Label sits ABOVE the
 *  swatch so the cell matches the sliders' height for even rows. */
const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <Box sx={fieldCellSx}>
    <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: 42, height: 26, border: '1px solid rgba(128,128,128,0.4)', borderRadius: 4, padding: 0, background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
      />
      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{value}</Typography>
    </Box>
  </Box>
);

const sliderSx = { mt: -0.25, py: 0.5 } as const;

const OpacitySlider: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <Box sx={fieldCellSx}>
    <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>{label}: {value.toFixed(2)}</Typography>
    <Slider
      size="small" min={0} max={1} step={0.01}
      value={value}
      onChange={(_, v) => onChange(v as number)}
      sx={sliderSx}
    />
  </Box>
);

/** R115 follow-up — line width as a slider (range 1-4, step 1). Replaces the
 *  numeric TextField for every "width" field in the dialog. */
const LineWidthSlider: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <Box sx={fieldCellSx}>
    <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>{label}: {value}</Typography>
    <Slider size="small" min={1} max={4} step={1} marks
      value={value} onChange={(_, v) => onChange(v as number)}
      sx={sliderSx}
    />
  </Box>
);

/** R115 follow-up — generic period slider (numeric range). */
const PeriodSlider: React.FC<{ label: string; value: number; min: number; max: number; step?: number; onChange: (v: number) => void }> = ({ label, value, min, max, step = 1, onChange }) => (
  <Box sx={fieldCellSx}>
    <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>{label}: {value}</Typography>
    <Slider size="small" min={min} max={max} step={step}
      value={value} onChange={(_, v) => onChange(v as number)}
      sx={sliderSx}
    />
  </Box>
);

/**
 * Sorts the cards inside a tab by their title, case-insensitively.
 *
 * Done structurally rather than by reordering the JSX, for two reasons: this
 * file is ~2,500 lines of nested markup and shuffling blocks by hand is a good
 * way to lose one, and a manual sort rots the moment the next indicator is
 * added. Every direct child of every tab is a <Section>, so sorting on the
 * `title` prop is total; anything without a title keeps its place at the top
 * rather than being silently dropped to the end.
 */
const SortedSections: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const kids = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement[];
  const titled = kids.filter(k => typeof (k.props as any)?.title === 'string');
  const rest = kids.filter(k => typeof (k.props as any)?.title !== 'string');
  titled.sort((a, b) => String((a.props as any).title)
    .localeCompare(String((b.props as any).title), undefined, { sensitivity: 'base', numeric: true }));
  return <>{[...rest, ...titled]}</>;
};

/** Collapsible section used inside each tab (R75). First section per tab is
 *  default-expanded so the panel opens informatively; the rest collapse to
 *  keep the visual surface area tight. */
const Section: React.FC<{ title: string; subtitle?: string; defaultExpanded?: boolean; children: React.ReactNode }> = ({
  title, subtitle, defaultExpanded = false, children,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      '&:before': { display: 'none' },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1.5,
      mb: 1,
      overflow: 'hidden',
      '&.Mui-expanded': { mb: 1 },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon fontSize="small" />}
      sx={{
        minHeight: 44, px: 1.5,
        // Faint header tint so each section reads as its own card and the
        // title row separates cleanly from the controls below.
        bgcolor: 'action.hover',
        '& .MuiAccordionSummary-content': { my: 0.75 },
        '&.Mui-expanded': { minHeight: 44, borderBottom: '1px solid', borderColor: 'divider' },
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.25 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 1.5, pb: 1.75, px: 1.75 }}>{children}</AccordionDetails>
  </Accordion>
);

const ChartSettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const ctx = useChartSettings();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ChartSettingsPayload>(DEFAULT_CHART_SETTINGS);
  const [name, setName] = useState('Default');
  const [saving, setSaving] = useState(false);
  // R115 Stage 8 — debounced auto-save state: shows "Saving…" / "Saved ✓" so
  // the user knows their tweaks persist live without clicking Save.
  const [autoSavedAt, setAutoSavedAt] = useState<number | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const skipNextAutoSaveRef = React.useRef(true);
  // R75 — collapsed from 8 tabs to 5 themed tabs.
  type TabKey = 'candles' | 'lines' | 'bands' | 'indicators' | 'general';
  const [tab, setTab] = useState<TabKey>('candles');

  // Initialise draft from the active template when the dialog opens or the
  // active template changes underneath us.
  useEffect(() => {
    if (!open) return;
    skipNextAutoSaveRef.current = true; // initial draft load shouldn't trigger autosave
    const target = ctx.active || ctx.templates[0] || null;
    if (target) {
      setEditingId(target.id);
      setDraft(resolvePayload(target.payload));
      setName(target.name);
    } else {
      setEditingId(null);
      setDraft(DEFAULT_CHART_SETTINGS);
      setName('Default');
    }
  }, [open, ctx.active, ctx.templates]);

  // R115 Stage 8 — debounced auto-save on draft change. Skipped when there's
  // no editingId (i.e. the template hasn't been created yet — user must click
  // Create/Save once first) and right after a draft (re)load. 600ms quiet
  // window catches keystrokes; sliders fire continuously and naturally
  // collapse into one save when the user stops moving them.
  useEffect(() => {
    if (!open || editingId == null) return;
    if (skipNextAutoSaveRef.current) { skipNextAutoSaveRef.current = false; return; }
    const t = setTimeout(async () => {
      try {
        setAutoSaving(true);
        await ctx.updateTemplate(editingId, { name: name.trim(), payload: draft });
        setAutoSavedAt(Date.now());
      } catch {
        // Silent — explicit Save will surface the error via toast.
      } finally {
        setAutoSaving(false);
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, name, editingId, open]);

  const templates = ctx.templates;

  const handleSelectTemplate = (id: number) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setEditingId(id);
    setDraft(resolvePayload(t.payload));
    setName(t.name);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Template needs a name'); return; }
    setSaving(true);
    try {
      if (editingId != null) {
        await ctx.updateTemplate(editingId, { name: name.trim(), payload: draft });
        toast.success('Template saved');
      } else {
        const t = await ctx.createTemplate(name.trim(), draft, true);
        setEditingId(t.id);
        toast.success('Template created');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    const base = editingId != null ? `${name} copy` : name;
    setSaving(true);
    try {
      const t = await ctx.createTemplate(base, draft, false);
      setEditingId(t.id);
      setName(t.name);
      toast.success('Template duplicated');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Duplicate failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (editingId == null) return;
    try {
      await ctx.activateTemplate(editingId);
      toast.success('Template activated');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Activate failed');
    }
  };

  const handleDelete = async () => {
    if (editingId == null) return;
    if (!window.confirm(`Delete template "${name}"? This cannot be undone.`)) return;
    try {
      await ctx.deleteTemplate(editingId);
      toast.success('Template deleted');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Delete failed');
    }
  };

  const handleReset = () => {
    setDraft(DEFAULT_CHART_SETTINGS);
    toast.success('Reset to defaults (not yet saved)');
  };

  const patch = <K extends keyof ChartSettingsPayload>(key: K, partial: Partial<ChartSettingsPayload[K]>) => {
    setDraft(d => ({ ...d, [key]: { ...(d[key] as any), ...partial } }));
  };

  // R105 — per-indicator placement toggle: ON = render in the shared lower
  // indicator pane (own axis, no candle overlap); OFF = overlay on the price
  // chart (the classic behaviour). Drag the pane's top separator to resize it.
  const paneToggle = (key: 'relStrength' | 'dayChange' | 'macd' | 'jhm' | 'wrPct' | 'trend' | 'bbr' | 'wave') => (
    <FormControlLabel
      sx={{ mb: 0.5 }}
      control={<Switch size="small"
        checked={(draft as any)[key]?.pane !== false}
        onChange={ev => patch(key as any, { pane: ev.target.checked } as any)} />}
      label={<Typography variant="caption">Separate lower pane <em>(off = overlay on price chart)</em></Typography>}
    />
  );

  /** The higher-timeframe controls, identical on the BBR and Wave cards. The
   *  keys are declared on both groups' defaults (showHtf / htfColor /
   *  htfLineWidth); the curve itself is drawn by TradingChart via utils/oscHtf.ts. */
  const htfControls = (key: 'bbr' | 'wave', g: any, dfltColor: string, name: string) => (
    <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
      <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={g?.showLtf !== false}
            onChange={ev => patch(key as any, { showLtf: ev.target.checked } as any)} />}
          label={<Typography variant="caption">Show the <strong>chart timeframe</strong> {name}</Typography>} />
        <FormControlLabel
          control={<Switch size="small" checked={g?.showHtf === true}
            onChange={ev => patch(key as any, { showHtf: ev.target.checked } as any)} />}
          label={<Typography variant="caption">Show the <strong>higher timeframe</strong> {name}</Typography>} />
        <ColorField label="HTF line (slope colour off)" value={g?.htfColor || dfltColor}
          onChange={v => patch(key as any, { htfColor: v } as any)} />
        <LineWidthSlider label="HTF line width" value={g?.htfLineWidth ?? 3}
          onChange={n => patch(key as any, { htfLineWidth: n } as any)} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Draws {name} of the higher timeframe mapped on the Strategies page (e.g. 1H → 1D) in the same pane, on the same
        overbought / oversold rows, labelled with its timeframe (e.g. “{name} 1D”). Each higher-timeframe value is drawn
        flat across the bars of its own period, coloured rising / falling like the chart curve (the colours and the
        switch are in the BBR × Wave card; the HTF line colour here is used when that switch is off). The current,
        unfinished period is dashed because its value still moves with price. Turn the chart-timeframe curve off to
        read the higher timeframe alone — the guides, zones and the BW / BD / X marks on the price chart stay, since
        hiding a line does not change a signal. Nothing is drawn for the higher timeframe when there is none (e.g. on
        1W, the top of the ladder).
      </Typography>
    </Box>
  );

  const activeFlag = useMemo(
    () => (editingId != null ? templates.find(t => t.id === editingId)?.isActive : false),
    [editingId, templates]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperComponent={DraggablePaper} aria-labelledby="chart-settings-dialog-title">
      <DialogTitle id="chart-settings-dialog-title" className="chartsettings-drag-handle"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, cursor: 'move', userSelect: 'none' }}>
        <Typography variant="h6" sx={{ flex: 1 }}>Chart Settings <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>(drag title to move)</Typography></Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Template picker */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={editingId ?? ''}
              onChange={e => handleSelectTemplate(Number(e.target.value))}
              displayEmpty
            >
              {templates.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.isActive ? '★ ' : ''}{t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small" label="Name" value={name}
            onChange={e => setName(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {editingId != null ? 'Save' : 'Create'}
          </Button>
          {/* R115 Stage 8 — auto-save indicator. Visible only when editing an
              existing template (autosave path). */}
          {editingId != null && (
            <Typography variant="caption" sx={{
              ml: 0.5, fontWeight: 700,
              color: autoSaving ? 'text.secondary' : (autoSavedAt ? 'success.main' : 'text.disabled'),
              fontFamily: 'monospace', minWidth: 88,
            }}>
              {autoSaving ? 'Saving…' : autoSavedAt ? '✓ Saved' : ''}
            </Typography>
          )}
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleDuplicate} disabled={saving}>
            Duplicate
          </Button>
          {editingId != null && !activeFlag && (
            <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleIcon />} onClick={handleActivate}>
              Activate
            </Button>
          )}
          {editingId != null && (
            <Tooltip title={activeFlag ? 'Activate another template before deleting this one' : ''}>
              <span>
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete} disabled={!!activeFlag}>
                  Delete
                </Button>
              </span>
            </Tooltip>
          )}
          <Button size="small" onClick={handleReset}>Reset Defaults</Button>
        </Stack>

        {!activeFlag && editingId != null && (
          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            This template is not active. Activate to see your changes on the charts.
          </Alert>
        )}

        {/* Tabbed organisation — R75 consolidated 8 tabs into 5 themed groups.
            Within each tab, collapsible Sections (Accordions) keep related
            controls together while letting the user collapse what they're
            not currently editing. */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ mb: 1.5, minHeight: 38, '& .MuiTab-root': { minHeight: 38, fontSize: '0.8rem', textTransform: 'none', fontWeight: 700 } }}
        >
          <Tab label="Candles"        value="candles" />
          <Tab label="Lines"          value="lines" />
          <Tab label="Bands & Clouds" value="bands" />
          <Tab label="Indicators"     value="indicators" />
          <Tab label="General"        value="general" />
        </Tabs>

        {/* ─────────────────── TAB 1 — CANDLES ─────────────────── */}
        {tab === 'candles' && (
          <Box>
            <SortedSections>
            <Section title="Candle colours" subtitle="Rising / falling fill colours used by the candle series." defaultExpanded>
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1 }}>
                <ColorField label="Rising"  value={draft.candle.risingColor}
                  onChange={v => patch('candle', { risingColor: v })} />
                <ColorField label="Falling" value={draft.candle.fallingColor}
                  onChange={v => patch('candle', { fallingColor: v })} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Candle wicks on/off is now a quick toggle on the Charts toolbar (next to the candle-type buttons).
              </Typography>
            </Section>

            <Section title="LR body (CLN)"
              subtitle="A wickless linear-regression body of the real opens and closes. The CLN candle type is the real candle + this body; the LR chip on the toolbar draws the same body over ANY candle type. When the candles are ghosted, BOTH layers take their colour from the LR direction, so a red bar inside a rising regression prints bullish — that disagreement is the point. The Wick toggle controls the candles only; the regression layer is never given wicks.">
              <Stack direction="row" useFlexGap spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <TextField
                  size="small" type="number" label="Regression length"
                  value={draft.candle.clnLength ?? 14}
                  onChange={e => patch('candle', { clnLength: Math.max(2, Math.min(200, Number(e.target.value) || 14)) })}
                  sx={{ width: 200 }}
                  inputProps={{ min: 2, max: 200 }}
                  helperText="2–200; default 14. Shorter = faster, noisier bodies."
                />
                <FormControlLabel
                  control={<Switch checked={draft.candle.clnVolumeAccent === true}
                    onChange={ev => patch('candle', { clnVolumeAccent: ev.target.checked })} />}
                  label={<Typography variant="caption">Outline regression bodies printed on thin volume</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={draft.candle.lrGhost !== false}
                    onChange={ev => patch('candle', { lrGhost: ev.target.checked })} />}
                  label={<Typography variant="caption">LR chip: ghost the candles under the body (CLN look)</Typography>}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                The outline is a diagnostic only — volume can never change a candle's colour or its price geometry.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                The higher-timeframe context band is now its own indicator: the <strong>HTF Band</strong> chip
                (Bands &amp; Levels), with its settings in the HTF Band card.
              </Typography>
            </Section>

            <Section title="Important fraction — build candles from the end of each bar"
              subtitle="Builds every candle type (Reg, HA, CLH, CLN) from only the LAST part of each bar's time. With 1/3 on a 30-minute chart, each candle's open, high, low and volume come from its last 10 minutes; the close is the bar's real close. 1 = normal candles, exactly as before.">
              <Stack direction="row" useFlexGap spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Important fraction</InputLabel>
                  <Select
                    label="Important fraction"
                    value={normaliseDenominator(draft.candleFraction?.denominator)}
                    onChange={e => patch('candleFraction', { denominator: normaliseDenominator(e.target.value) })}
                  >
                    {FRACTION_DENOMINATORS.map(d => (
                      <MenuItem key={d} value={d}>{d === 1 ? '1 — normal candles' : `1/${d} — the last ${d === 2 ? 'half' : d === 3 ? 'third' : d === 4 ? 'quarter' : `1/${d}`} of each bar`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Candles only: indicators, strategies, scans, alerts, orders and P&amp;L keep using the real bars, and the
                OHLC legend shows the real prices with the fraction prices beside them. It is a way of looking at
                candles, not a tested edge: a study of 40 Delta symbols found the last part of a bar did not predict
                the next move better than the whole bar.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Needs the bars' insides (finer bars), so older history the venue no longer serves at that detail stays
                normal and faded; the legend says from which date fraction candles start. Option panes and synthetic
                symbols stay normal. A still-forming bar stays normal until its last part begins.
              </Typography>
            </Section>

            <Section title="P-Zone — profit zone reference"
              subtitle="Turned on with the P-Zone chip. Two amber levels beyond the HTF Band in the direction it is moving: Zone 1 and Zone 2 sit m1 and m2 × the average bar range (ATR 50) past the band's edge, set at each higher-timeframe close and held flat until the next one. Nothing is drawn while the HTF Band is mixed or flat.">
              <Stack direction="row" useFlexGap spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <TextField size="small" type="number" label="Zone 1 (× ATR)" sx={{ width: 140 }}
                  value={draft.profitZone?.m1 ?? 2.5} inputProps={{ min: 0.5, max: 10, step: 0.5 }}
                  onChange={e => patch('profitZone', { m1: Math.max(0.5, Math.min(10, Number(e.target.value) || 2.5)) })} />
                <TextField size="small" type="number" label="Zone 2 (× ATR)" sx={{ width: 140 }}
                  value={draft.profitZone?.m2 ?? 4} inputProps={{ min: 0.5, max: 15, step: 0.5 }}
                  onChange={e => patch('profitZone', { m2: Math.max(0.5, Math.min(15, Number(e.target.value) || 4)) })} />
                <ColorField label="Colour" value={draft.profitZone?.color || '#f5a623'}
                  onChange={v => patch('profitZone', { color: v })} />
                <TextField size="small" type="number" label="Fill opacity" sx={{ width: 130 }}
                  value={draft.profitZone?.fillOpacity ?? 0.1} inputProps={{ min: 0, max: 0.4, step: 0.02 }}
                  onChange={e => patch('profitZone', { fillOpacity: Math.max(0, Math.min(0.4, Number(e.target.value))) })} />
                <TextField size="small" type="number" label="Line width" sx={{ width: 110 }}
                  value={draft.profitZone?.lineWidth ?? 1} inputProps={{ min: 1, max: 4, step: 1 }}
                  onChange={e => patch('profitZone', { lineWidth: Math.max(1, Math.min(4, Math.round(Number(e.target.value) || 1))) })} />
                <FormControlLabel
                  control={<Switch checked={draft.profitZone?.fitScale !== false}
                    onChange={ev => patch('profitZone', { fitScale: ev.target.checked })} />}
                  label={<Typography variant="caption">Keep the zones on screen (they widen the price scale)</Typography>}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                A reference, not a tested signal: in a study of 220 Delta symbols, booking part of a trade at Zone 1 was
                no better than booking at a random profitable moment. 2.5 and 4 are the study's values; tuning them by eye
                to fit past turns is curve-fitting. Each step uses the higher-timeframe bar that has already closed, so
                the zones never move inside a period and sit one period to the right of the HTF Band step they come from.
              </Typography>
            </Section>

            <Section title="HTF Band — higher-timeframe CLN context band"
              subtitle="Turned on with the HTF Band chip. Runs the CLN regression on the higher timeframe mapped to this chart's interval (Strategies → Timeframe Mapping) and draws a ribbon of those bodies behind the candles: one step per HTF bar, coloured by that bar's own regression direction (the candle rising / falling colours). Context, not a signal; works with any candle type. A 1W chart has no higher timeframe, so it draws nothing there.">
              <Stack direction="row" useFlexGap spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={(draft as any).htfBand?.showForming !== false}
                    onChange={ev => patch('htfBand' as any, { showForming: ev.target.checked } as any)} />}
                  label={<Typography variant="caption">Include the forming HTF bar</Typography>}
                />
                <Box sx={{ width: 220 }}>
                  <OpacitySlider label="Band opacity" value={(draft as any).htfBand?.opacity ?? 0.12}
                    onChange={v => patch('htfBand' as any, { opacity: v } as any)} />
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                <strong>Include the forming HTF bar</strong> carries the band all the way to the last candle
                using the higher-timeframe bar that is still open. That last segment REPAINTS as the bar
                develops, so it is drawn with a lighter fill and a dashed edge. Turn it off to see only
                closed, settled context.
              </Typography>
            </Section>

            <Section title="Layout — remembered zoom"
              subtitle="The chart REMEMBERS your last zoom per layout (single / split / quad): scroll-zoom to the candle count you like and every new symbol opens at that zoom for that layout. The empty space after the last candle is no longer a setting — it is a fixed share of the view (~10%), so it looks the same on a 1-minute chart and a monthly one instead of meaning 20 minutes on the first and 20 months on the second.">
              <Stack direction="row" useFlexGap spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Button
                  size="small" variant="outlined"
                  onClick={() => { resetVisibleBars(); toast.success('Remembered zoom reset — new charts open at the default candle count'); }}
                >
                  Reset remembered zoom
                </Button>
              </Stack>
            </Section>

            <Section title="Live update cadence"
              subtitle="How often the chart polls for fresh candles and Delta mark price. Smaller = snappier, larger = lighter on broker rate limits.">
              <TextField
                size="small" type="number" label="Poll interval (sec)"
                value={draft.liveUpdateSec}
                onChange={e => setDraft(d => ({ ...d, liveUpdateSec: Math.max(5, Math.min(120, Number(e.target.value) || 5)) }))}
                sx={{ width: 220 }}
                inputProps={{ min: 5, max: 120 }}
                helperText="Min 5s, max 120s"
              />
            </Section>

            <Section title="Crosshair tooltip"
              subtitle="The info box that follows the cursor (OHLC / Trend / Strategy / VPA). Turn it off when analysing split layouts where the box overlaps the other pane. Crosshair sync between panes is unaffected.">
              <FormControlLabel
                control={<Switch checked={draft.crosshairTooltip !== false}
                  onChange={ev => setDraft(d => ({ ...d, crosshairTooltip: ev.target.checked }))} />}
                label={<Typography variant="caption">Show crosshair tooltip</Typography>}
              />
            </Section>

            <Section title="Candle highlights — BBR tint + Volume spike"
              subtitle="Override-priority candle colours. These win over strategy-driven colours when the matching overlays (BBR tint / VSpk) are enabled. The BBR OSCILLATOR CURVE is a separate thing with its own section under Indicators — these two colours also shade its oversold / overbought zones, so the tint and the curve stay visually the same indicator.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <ColorField label="BBR overbought" value={draft.bbr.overboughtColor}
                  onChange={v => patch('bbr', { overboughtColor: v })} />
                <ColorField label="BBR oversold"   value={draft.bbr.oversoldColor}
                  onChange={v => patch('bbr', { oversoldColor: v })} />
                <ColorField label="Volume spike"   value={draft.vspk.color}
                  onChange={v => patch('vspk', { color: v })} />
              </Stack>
            </Section>

            <Section title="Grid" subtitle="Background grid intensity (0 = hidden).">
              <OpacitySlider label="Grid opacity" value={draft.grid.opacity}
                onChange={v => patch('grid', { opacity: v })} />
            </Section>
            </SortedSections>
          </Box>
        )}

        {/* ─────────────────── TAB 2 — LINES ─────────────────── */}
        {tab === 'lines' && (
          <Box>
            <SortedSections>
            <Section title="LTP line"
              subtitle="Always tracks the actual last-traded price — stays anchored to market even on Heikin-Ashi or close-style transforms."
              defaultExpanded>
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <ColorField label="LTP color" value={draft.ltp.lineColor}
                  onChange={v => patch('ltp', { lineColor: v })} />
                <LineWidthSlider label="LTP width" value={draft.ltp.lineWidth}
                  onChange={v => patch('ltp', { lineWidth: v })} />
              </Stack>
            </Section>

            <Section title="Mark price line (Delta perpetuals)"
              subtitle="Toggle the dotted mark-price line. Disabling the line still keeps the live-candle tick behaviour on Delta — it just hides the visual.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={draft.markPrice.enabled}
                    onChange={e => patch('markPrice', { enabled: e.target.checked })} />}
                  label="Show mark price line"
                />
                <ColorField label="Mark color" value={draft.markPrice.color}
                  onChange={v => patch('markPrice', { color: v })} />
              </Stack>
            </Section>

            <Section title="Session start lines"
              subtitle="Vertical lines at session opens or option-expiry boundaries. Default mode follows the chart's LTF (≤15m every session, >15m–4h weekly, >4h monthly); the expiry modes anchor to the relevant option expiry instead.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={draft.sessions.enabled}
                    onChange={e => patch('sessions', { enabled: e.target.checked })} />}
                  label="Show session lines"
                />
                <ColorField label="Color" value={draft.sessions.color}
                  onChange={v => patch('sessions', { color: v })} />
                <OpacitySlider label="Opacity" value={draft.sessions.opacity}
                  onChange={v => patch('sessions', { opacity: v })} />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Style</InputLabel>
                  <Select
                    label="Style"
                    value={draft.sessions.style}
                    onChange={e => patch('sessions', { style: e.target.value as SessionLineStyle })}
                  >
                    <MenuItem value="solid">Solid</MenuItem>
                    <MenuItem value="dashed">Dashed</MenuItem>
                    <MenuItem value="dotted">Dotted</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {/* Mode selector + per-mode anchor config */}
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mt: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    label="Mode"
                    value={draft.sessions.mode ?? 'default'}
                    onChange={e => patch('sessions', { mode: e.target.value as 'default' | 'deltaExpiry' | 'nseWeekly' | 'nseMonthly' })}
                  >
                    <MenuItem value="default">Default (session open)</MenuItem>
                    <MenuItem value="deltaExpiry">Delta option expiry (daily)</MenuItem>
                    <MenuItem value="nseWeekly">NSE weekly option expiry</MenuItem>
                    <MenuItem value="nseMonthly">NSE monthly option expiry</MenuItem>
                  </Select>
                </FormControl>

                {draft.sessions.mode === 'deltaExpiry' && (
                  <TextField size="small" label="Delta expiry time (IST)" sx={{ width: 170 }}
                    value={draft.sessions.deltaExpiryTime ?? '17:30'}
                    onChange={e => patch('sessions', { deltaExpiryTime: e.target.value })}
                    placeholder="HH:MM" helperText="Daily, incl. weekends" />
                )}

                {(draft.sessions.mode === 'nseWeekly' || draft.sessions.mode === 'nseMonthly') && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Expiry day</InputLabel>
                      <Select
                        label="Expiry day"
                        value={draft.sessions.nseExpiryDow ?? 2}
                        onChange={e => patch('sessions', { nseExpiryDow: Number(e.target.value) })}
                      >
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => (
                          <MenuItem key={i} value={i}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Expiry time (IST)" sx={{ width: 160 }}
                      value={draft.sessions.nseExpiryTime ?? '15:30'}
                      onChange={e => patch('sessions', { nseExpiryTime: e.target.value })}
                      placeholder="HH:MM"
                      helperText={draft.sessions.mode === 'nseMonthly' ? 'Last such day of month' : 'Weekly'} />
                  </>
                )}
              </Stack>
            </Section>
            </SortedSections>
          </Box>
        )}

        {/* ─────────────────── TAB 3 — BANDS & CLOUDS ─────────────────── */}
        {tab === 'bands' && (
          <Box>
            <SortedSections>
            <Section title="CPR — Central Pivot Range"
              subtitle="Per-period TC/BC band with optional pivot midline. Colour reflects band-rise direction vs the previous period."
              defaultExpanded>
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1 }}>
                <OpacitySlider label="CPR fill opacity" value={draft.cpr.opacity}
                  onChange={v => patch('cpr', { opacity: v })} />
                <ColorField label="Rising"  value={draft.cpr.risingColor}
                  onChange={v => patch('cpr', { risingColor: v })} />
                <ColorField label="Falling" value={draft.cpr.fallingColor}
                  onChange={v => patch('cpr', { fallingColor: v })} />
                <ColorField label="Neutral" value={draft.cpr.neutralColor}
                  onChange={v => patch('cpr', { neutralColor: v })} />
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Switch checked={draft.cpr.showMidline}
                    onChange={e => patch('cpr', { showMidline: e.target.checked })} />}
                  label="Show CPR midline (pivot)"
                />
                <FormControlLabel
                  control={<Switch checked={draft.cpr.showProjection}
                    onChange={e => patch('cpr', { showProjection: e.target.checked })} />}
                  label="Show projected next-period CPR"
                />
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.6 }}>
                Rising = TC/BC moves up vs previous period (one up, none down). Falling = mirror. Neutral = otherwise. Projection uses the developing period's HLC.
              </Typography>
            </Section>

            <Section title="POC — Point of Control (C-POC + H-POC)"
              subtitle="Volume-profile POC band(s). Same Rising/Falling/Neutral logic as CPR, applied independently to each.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1 }}>
                <OpacitySlider label="POC fill opacity" value={draft.poc.opacity}
                  onChange={v => patch('poc', { opacity: v })} />
                <ColorField label="Rising"  value={draft.poc.risingColor}
                  onChange={v => patch('poc', { risingColor: v })} />
                <ColorField label="Falling" value={draft.poc.fallingColor}
                  onChange={v => patch('poc', { fallingColor: v })} />
                <ColorField label="Neutral" value={draft.poc.neutralColor}
                  onChange={v => patch('poc', { neutralColor: v })} />
              </Stack>
              <FormControlLabel
                control={<Switch checked={draft.poc.showProjection}
                  onChange={e => patch('poc', { showProjection: e.target.checked })} />}
                label="Show projected next-period POC"
              />
            </Section>

            <Section title="MM — Market Maker staircase"
              subtitle="Marks the body (Open/Close) of each SOURCE candle that is a BBR reversal OR a Vol spike, joined into a condition-driven staircase. Bull fill when the stair steps up, bear when down. The source candle type is independent of the chart's display candle type.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1 }} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Source candle type</InputLabel>
                  <Select
                    value={(draft as any).mm?.candleType || 'Regular'}
                    label="Source candle type"
                    onChange={ev => patch('mm' as any, { candleType: ev.target.value } as any)}
                  >
                    <MenuItem value="Regular">Regular</MenuItem>
                    <MenuItem value="HA">HA — Heikin-Ashi</MenuItem>
                    <MenuItem value="CLH">CLH — Custom Linreg HA</MenuItem>
                  </Select>
                </FormControl>
                <ColorField label="Bullish (steps up)"   value={(draft as any).mm?.bullColor || '#26a69a'}
                  onChange={v => patch('mm' as any, { bullColor: v } as any)} />
                <ColorField label="Bearish (steps down)" value={(draft as any).mm?.bearColor || '#ef5350'}
                  onChange={v => patch('mm' as any, { bearColor: v } as any)} />
                <OpacitySlider label="Band opacity" value={(draft as any).mm?.opacity ?? 0.22}
                  onChange={v => patch('mm' as any, { opacity: v } as any)} />
              </Stack>
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
                <FormControlLabel
                  control={<Switch checked={(draft as any).mm?.showLtf !== false}
                    onChange={ev => patch('mm' as any, { showLtf: ev.target.checked } as any)} />}
                  label={<Typography variant="caption">Chart timeframe</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={(draft as any).mm?.showHtf === true}
                    onChange={ev => patch('mm' as any, { showHtf: ev.target.checked } as any)} />}
                  label={<Typography variant="caption">Higher timeframe</Typography>}
                />
                <Box sx={{ width: 220 }}>
                  <OpacitySlider label="Higher-timeframe opacity" value={(draft as any).mm?.htfOpacity ?? 0.12}
                    onChange={v => patch('mm' as any, { htfOpacity: v } as any)} />
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                <strong>Higher timeframe</strong> draws the same staircase for the timeframe mapped to this chart
                (Strategies → Timeframe Mapping), with dashed edges. A higher-timeframe mark exists only once its
                bar has CLOSED, so each of its stairs starts at the first chart candle after that close, never
                across the higher-timeframe bar itself. On 1W there is no higher timeframe, so nothing is drawn.
              </Typography>
            </Section>

            <Section title="LR_HAMA_Band — Avg SLR+HAMA O/C Cloud"
              subtitle="Average of SLR (CSL O/C) and HAMA (O/C) clouds. Independent of the chart's display candle type.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <ColorField label="Band color" value={draft.lrBnd.color}
                  onChange={v => patch('lrBnd', { color: v })} />
                <OpacitySlider label="Band opacity" value={draft.lrBnd.opacity}
                  onChange={v => patch('lrBnd', { opacity: v })} />
              </Stack>
            </Section>

            <Section title="AvgCloud — Dynamic LR_HAMA ↔ CMid Fill"
              subtitle="Translucent fill between the LR_HAMA_Band edge and the Combined Midline. Bullish above CMid, Bearish below.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1 }}>
                <ColorField label="Bullish (above CMid)" value={draft.avgCloud.bullishColor}
                  onChange={v => patch('avgCloud', { bullishColor: v })} />
                <ColorField label="Bearish (below CMid)" value={draft.avgCloud.bearishColor}
                  onChange={v => patch('avgCloud', { bearishColor: v })} />
                <ColorField label="Neutral"               value={draft.avgCloud.neutralColor}
                  onChange={v => patch('avgCloud', { neutralColor: v })} />
                <OpacitySlider label="Fill opacity" value={draft.avgCloud.opacity}
                  onChange={v => patch('avgCloud', { opacity: v })} />
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                CMid is computed from CTF + HTF history (~1000 bars). Give a busy symbol a few seconds after enabling this overlay so CMid converges.
              </Typography>
            </Section>

            <Section title="Combined Midline (CMid)"
              subtitle="Dashed midline of the combined 3-timeframe envelope (LTF + CTF + HTF Bollinger blend).">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <ColorField label="CMid color" value={draft.cMid.color}
                  onChange={v => patch('cMid', { color: v })} />
                <LineWidthSlider label="CMid width" value={draft.cMid.lineWidth}
                  onChange={v => patch('cMid', { lineWidth: v })} />
              </Stack>
            </Section>
            </SortedSections>
          </Box>
        )}

        {/* ─────────────────── TAB 4 — INDICATORS ─────────────────── */}
        {tab === 'indicators' && (
          <Box>
            <SortedSections>
            <Section title="EMAs — over LinReg(close, 14)"
              subtitle="Four independently-configurable EMAs. Each shows on the chart only when its row toggle is ON."
              defaultExpanded>
              {(['ema1', 'ema2', 'ema3', 'ema4'] as const).map((k, i) => {
                const e = (draft as any).emas?.[k] || { period: [7, 21, 51, 200][i], enabled: true, color: ['#ff7043', '#ffd54f', '#26c6da', '#ab47bc'][i] };
                return (
                  <Stack key={k} direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={e.enabled !== false}
                          onChange={ev => setDraft(d => ({ ...d, emas: { ...(d as any).emas, [k]: { ...e, enabled: ev.target.checked } } } as any))}
                        />
                      }
                      label={<Typography variant="caption" sx={{ width: 56 }}>EMA #{i + 1}</Typography>}
                    />
                    <PeriodSlider label="Period" value={e.period} min={5} max={250}
                      onChange={n => setDraft(d => ({ ...d, emas: { ...(d as any).emas, [k]: { ...e, period: n } } } as any))} />
                    <ColorField
                      label="Color"
                      value={e.color}
                      onChange={v => setDraft(d => ({ ...d, emas: { ...(d as any).emas, [k]: { ...e, color: v } } } as any))}
                    />
                  </Stack>
                );
              })}
            </Section>

            <Section title="RelStrength — vs Benchmark"
              subtitle="LinReg(close,14) of the chart symbol ÷ same on benchmark, normalised to 100. Leave benchmark blank to auto-resolve (Crypto → BTCUSD; BTCUSD → ETHUSD; NSE stock → NIFTY 50; NIFTY 50 → BANK NIFTY).">
              {paneToggle('relStrength')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <TextField
                  label="Benchmark symbol"
                  size="small"
                  value={(draft as any).relStrength?.benchmarkSymbol || ''}
                  onChange={ev => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, benchmarkSymbol: ev.target.value.toUpperCase() } } as any))}
                  placeholder="(auto)"
                  sx={{ width: 180 }}
                />
                <TextField
                  label="Benchmark exchange"
                  size="small"
                  value={(draft as any).relStrength?.benchmarkExchange || ''}
                  onChange={ev => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, benchmarkExchange: ev.target.value.toUpperCase() } } as any))}
                  placeholder="(auto)"
                  sx={{ width: 160 }}
                />
                <ColorField
                  label="Line color"
                  value={(draft as any).relStrength?.color || '#7e57c2'}
                  onChange={v => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, color: v } } as any))}
                />
                <LineWidthSlider label="Line width" value={(draft as any).relStrength?.lineWidth ?? 2}
                  onChange={n => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, lineWidth: n } } as any))} />
                <TextField
                  label="EMA period"
                  type="number"
                  size="small"
                  value={(draft as any).relStrength?.emaPeriod ?? 7}
                  onChange={ev => {
                    const n = Math.max(1, Math.min(200, parseInt(ev.target.value, 10) || 7));
                    setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, emaPeriod: n } } as any));
                  }}
                  inputProps={{ min: 1, max: 200, step: 1 }}
                  sx={{ width: 110 }}
                />
                <ColorField
                  label="Band up (≥ EMA)"
                  value={(draft as any).relStrength?.bandUpColor || '#26a69a'}
                  onChange={v => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, bandUpColor: v } } as any))}
                />
                <ColorField
                  label="Band down (< EMA)"
                  value={(draft as any).relStrength?.bandDownColor || '#ef5350'}
                  onChange={v => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, bandDownColor: v } } as any))}
                />
                <Box sx={{ width: 200 }}>
                  <OpacitySlider label="Band opacity" value={(draft as any).relStrength?.bandOpacity ?? 0.20}
                    onChange={v => setDraft(d => ({ ...d, relStrength: { ...(d as any).relStrength, bandOpacity: v } } as any))} />
                </Box>
              </Stack>
            </Section>

            <Section title="Synthiya — PCR · IV · RelStrength staircases"
              subtitle="Built for synthetic symbols (works on any optionable chart): the UNDERLYING's put-call ratio, ATM implied volatility and relative strength as staircase lines in the lower pane. Each step is coloured UP when it rises vs the previous step, DOWN when it falls.">
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                {/* `k` is a union, not a string, so `${k}UpColor` resolves to a real
                    key of settings.synthiya. That is the point: these fields used to be
                    written through `as any`, which is exactly why nobody noticed the
                    group was missing from DEFAULT_CHART_SETTINGS and being dropped on
                    every reload. Typed, a missing group is a compile error. */}
                {([
                  ['pcr', 'PCR', '#26a69a', '#ef5350', 2],
                  ['iv', 'IV', '#42a5f5', '#ef6c00', 1],
                  ['rs', 'RelStrength', '#9ccc65', '#ab47bc', 1],
                ] as Array<['pcr' | 'iv' | 'rs', string, string, string, number]>).map(([k, label, defUp, defDn, defW]) => (
                  <React.Fragment key={k}>
                    <ColorField
                      label={`${label} up`}
                      value={draft.synthiya?.[`${k}UpColor`] || defUp}
                      onChange={v => setDraft(d => ({ ...d, synthiya: { ...d.synthiya, [`${k}UpColor`]: v } }))}
                    />
                    <ColorField
                      label={`${label} down`}
                      value={draft.synthiya?.[`${k}DownColor`] || defDn}
                      onChange={v => setDraft(d => ({ ...d, synthiya: { ...d.synthiya, [`${k}DownColor`]: v } }))}
                    />
                    <LineWidthSlider label={`${label} width`} value={draft.synthiya?.[`${k}Width`] ?? defW}
                      onChange={n => setDraft(d => ({ ...d, synthiya: { ...d.synthiya, [`${k}Width`]: n } }))} />
                  </React.Fragment>
                ))}
              </Stack>
            </Section>

            <Section title="ChgN% — Rolling N-candle change"
              subtitle="Plots (close − close_N_bars_ago) ÷ close_N_bars_ago × 100. N = 24 by default; meaningful on any timeframe. EMA + band highlights momentum direction.">
              {paneToggle('dayChange')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <TextField
                  label="Lookback (N bars)"
                  type="number"
                  size="small"
                  value={(draft as any).dayChange?.lookbackBars ?? 24}
                  onChange={ev => {
                    const n = Math.max(1, Math.min(2000, parseInt(ev.target.value, 10) || 24));
                    setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, lookbackBars: n } } as any));
                  }}
                  inputProps={{ min: 1, max: 2000, step: 1 }}
                  sx={{ width: 140 }}
                />
                <ColorField
                  label="Line color"
                  value={(draft as any).dayChange?.color || '#00897b'}
                  onChange={v => setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, color: v } } as any))}
                />
                <TextField
                  label="Line width"
                  type="number"
                  size="small"
                  value={(draft as any).dayChange?.lineWidth ?? 2}
                  onChange={ev => {
                    const n = Math.max(1, Math.min(4, parseInt(ev.target.value, 10) || 2));
                    setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, lineWidth: n } } as any));
                  }}
                  inputProps={{ min: 1, max: 4, step: 1 }}
                  sx={{ width: 100 }}
                />
                <TextField
                  label="EMA period"
                  type="number"
                  size="small"
                  value={(draft as any).dayChange?.emaPeriod ?? 7}
                  onChange={ev => {
                    const n = Math.max(1, Math.min(200, parseInt(ev.target.value, 10) || 7));
                    setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, emaPeriod: n } } as any));
                  }}
                  inputProps={{ min: 1, max: 200, step: 1 }}
                  sx={{ width: 110 }}
                />
                <ColorField
                  label="Band up (≥ EMA)"
                  value={(draft as any).dayChange?.bandUpColor || '#26a69a'}
                  onChange={v => setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, bandUpColor: v } } as any))}
                />
                <ColorField
                  label="Band down (< EMA)"
                  value={(draft as any).dayChange?.bandDownColor || '#ef5350'}
                  onChange={v => setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, bandDownColor: v } } as any))}
                />
                <Box sx={{ width: 200 }}>
                  <OpacitySlider label="Band opacity" value={(draft as any).dayChange?.bandOpacity ?? 0.20}
                    onChange={v => setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, bandOpacity: v } } as any))} />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={(draft as any).dayChange?.showZeroLine !== false}
                      onChange={ev => setDraft(d => ({ ...d, dayChange: { ...(d as any).dayChange, showZeroLine: ev.target.checked } } as any))}
                    />
                  }
                  label={<Typography variant="caption">Show 0% line</Typography>}
                />
              </Stack>
            </Section>

            <Section title="MACD — band (CM_MacD)"
              subtitle="EMA(fast) − EMA(slow) on the CSH-smoothed close (always, regardless of the chart's candle type); signal = SMA(macd, signal length). Plotted as MACD + Signal lines with a band colored by the MACD line's slope — greenish while MACD is rising, reddish while falling. Defaults 12 / 26 / 9.">
              {paneToggle('macd')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                {/* F40 — sliders for the period fields. Sensible ranges
                    (Fast 5-50, Slow 10-100, Signal 5-30) cover every retail
                    MACD config; the previous TextField range of 1-400 was
                    too permissive to ever be useful. */}
                <PeriodSlider label="Fast"   value={(draft as any).macd?.fastLength   ?? 12} min={5}  max={50}
                  onChange={n => setDraft(d => ({ ...d, macd: { ...(d as any).macd, fastLength: n } } as any))} />
                <PeriodSlider label="Slow"   value={(draft as any).macd?.slowLength   ?? 26} min={10} max={100}
                  onChange={n => setDraft(d => ({ ...d, macd: { ...(d as any).macd, slowLength: n } } as any))} />
                <PeriodSlider label="Signal" value={(draft as any).macd?.signalLength ?? 9}  min={5}  max={30}
                  onChange={n => setDraft(d => ({ ...d, macd: { ...(d as any).macd, signalLength: n } } as any))} />
                <ColorField label="MACD line" value={(draft as any).macd?.macdColor || '#2962ff'} onChange={v => setDraft(d => ({ ...d, macd: { ...(d as any).macd, macdColor: v } } as any))} />
                <ColorField label="Signal line" value={(draft as any).macd?.signalColor || '#ff6d00'} onChange={v => setDraft(d => ({ ...d, macd: { ...(d as any).macd, signalColor: v } } as any))} />
                <ColorField label="Band up (rising)" value={(draft as any).macd?.bandUpColor || '#26a69a'} onChange={v => setDraft(d => ({ ...d, macd: { ...(d as any).macd, bandUpColor: v } } as any))} />
                <ColorField label="Band down (falling)" value={(draft as any).macd?.bandDownColor || '#ef5350'} onChange={v => setDraft(d => ({ ...d, macd: { ...(d as any).macd, bandDownColor: v } } as any))} />
                <Box sx={{ width: 200 }}>
                  <OpacitySlider label="Band opacity" value={(draft as any).macd?.bandOpacity ?? 0.20} onChange={v => setDraft(d => ({ ...d, macd: { ...(d as any).macd, bandOpacity: v } } as any))} />
                </Box>
              </Stack>
            </Section>

            <Section title="JHM — JSR Hilega-Milega (RSI oscillator)"
              subtitle="RSI of the source, then a VWMA and an EMA of that RSI, on a 0–100 strip with a 50 midline + bull/bear zone shading + EMA/VWMA fill. Bullish when EMA ≥ VWMA. Line/fill/dot colours use fixed theme-safe defaults; only the inputs below are adjustable.">
              {paneToggle('jhm')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <PeriodSlider label="RSI Length" value={(draft as any).jhm?.rsiLength ?? 9} min={5} max={50}
                  onChange={n => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, rsiLength: n } } as any))} />
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel>Source</InputLabel>
                  <Select label="Source" value={(draft as any).jhm?.source || 'close'}
                    onChange={e => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, source: e.target.value } } as any))}>
                    <MenuItem value="close">Close</MenuItem>
                    <MenuItem value="linregClose">Linear Regression Close</MenuItem>
                  </Select>
                </FormControl>
                <PeriodSlider label="VWMA Length" value={(draft as any).jhm?.vwmaLength ?? 21} min={5} max={100}
                  onChange={n => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, vwmaLength: n } } as any))} />
                <PeriodSlider label="EMA Length" value={(draft as any).jhm?.emaLength ?? 3} min={1} max={50}
                  onChange={n => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, emaLength: n } } as any))} />
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ColorField label="Bull shade (above 50)" value={(draft as any).jhm?.bullShadeColor || '#00C853'} onChange={v => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, bullShadeColor: v } } as any))} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Bull shade opacity" value={(draft as any).jhm?.bullShadeOpacity ?? 0.12} onChange={v => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, bullShadeOpacity: v } } as any))} />
                </Box>
                <ColorField label="Bear shade (below 50)" value={(draft as any).jhm?.bearShadeColor || '#FF1744'} onChange={v => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, bearShadeColor: v } } as any))} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Bear shade opacity" value={(draft as any).jhm?.bearShadeOpacity ?? 0.12} onChange={v => setDraft(d => ({ ...d, jhm: { ...(d as any).jhm, bearShadeOpacity: v } } as any))} />
                </Box>
              </Stack>
            </Section>

            <Section title="WR% — paired Williams %R EMAs"
              subtitle="Two parameterised Williams %R lines (fast %R + EMA, slow %R + EMA) on a fixed -100..0 strip with -20 overbought and -80 oversold guides. Crossover of fast EMA above slow EMA = bull (band shaded bull colour); below = bear. The pane scale is pinned so crossovers stay stable across scroll / zoom, which is the trigger source for the OverView WR% voter and any BUY/SHORT zone calls.">
              {paneToggle('wrPct')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel>Source</InputLabel>
                  <Select label="Source" value={(draft as any).wrPct?.source || 'close'}
                    onChange={e => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, source: e.target.value } } as any))}>
                    <MenuItem value="close">Close</MenuItem>
                    <MenuItem value="linregClose">Linear Regression Close</MenuItem>
                  </Select>
                </FormControl>
                <PeriodSlider label="Fast %R Length" value={(draft as any).wrPct?.fastLength ?? 21} min={5} max={100}
                  onChange={n => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, fastLength: n } } as any))} />
                <PeriodSlider label="Fast EMA" value={(draft as any).wrPct?.fastEma ?? 13} min={1} max={50}
                  onChange={n => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, fastEma: n } } as any))} />
                <PeriodSlider label="Slow %R Length" value={(draft as any).wrPct?.slowLength ?? 200} min={20} max={400}
                  onChange={n => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, slowLength: n } } as any))} />
                <PeriodSlider label="Slow EMA" value={(draft as any).wrPct?.slowEma ?? 13} min={1} max={50}
                  onChange={n => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, slowEma: n } } as any))} />
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ColorField label="Fast line" value={(draft as any).wrPct?.fastLineColor || '#FFA000'} onChange={v => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, fastLineColor: v } } as any))} />
                <ColorField label="Slow line" value={(draft as any).wrPct?.slowLineColor || '#00B8D4'} onChange={v => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, slowLineColor: v } } as any))} />
                <ColorField label="Bull band (fast above slow)" value={(draft as any).wrPct?.bullBandColor || '#00C853'} onChange={v => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, bullBandColor: v } } as any))} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Bull band opacity" value={(draft as any).wrPct?.bullBandOpacity ?? 0.22} onChange={v => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, bullBandOpacity: v } } as any))} />
                </Box>
                <ColorField label="Bear band (fast below slow)" value={(draft as any).wrPct?.bearBandColor || '#FF1744'} onChange={v => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, bearBandColor: v } } as any))} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Bear band opacity" value={(draft as any).wrPct?.bearBandOpacity ?? 0.22} onChange={v => setDraft(d => ({ ...d, wrPct: { ...(d as any).wrPct, bearBandOpacity: v } } as any))} />
                </Box>
              </Stack>
            </Section>

            <Section title="HVol — High / Weighted Volume (volume × close)"
              subtitle="Lower pane showing ONLY spike bars (raw wVol > Slow × 2) plus Fast(3)/Slow(21) EMAs of the LinReg(7)-smoothed wVol with a bull/bear band between them. The LinReg/EMA lengths and the spike multiplier are fixed; only the display below is adjustable. Cycle it on via the Flow / HVol / Vol chip (HVol position). The plain raw-volume 'Vol' option needs no settings.">
              {(() => {
                const v = (draft as any).vol || {};
                const setV = (p: any) => setDraft(d => ({ ...d, vol: { ...(d as any).vol, ...p } } as any));
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControlLabel control={<Switch checked={v.showEmas !== false} onChange={e => setV({ showEmas: e.target.checked })} />} label="Show Fast/Slow EMAs + band" />
                      <FormControlLabel control={<Switch checked={v.pane !== false} onChange={e => setV({ pane: e.target.checked })} />} label="Render in lower pane" />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <ColorField label="Fast EMA(3)" value={v.fastColor || '#29b6f6'} onChange={c => setV({ fastColor: c })} />
                      <ColorField label="Slow EMA(21)" value={v.slowColor || '#ffb74d'} onChange={c => setV({ slowColor: c })} />
                      <ColorField label="Bull band (fast ≥ slow)" value={v.bandBullColor || '#1565c0'} onChange={c => setV({ bandBullColor: c })} />
                      <ColorField label="Bear band (fast < slow)" value={v.bandBearColor || '#f57f17'} onChange={c => setV({ bandBearColor: c })} />
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Band opacity" value={v.bandOpacity ?? 0.35} onChange={n => setV({ bandOpacity: n })} />
                      </Box>
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="Bull spike bar" value={v.bullSpikeColor || '#ff6f00'} onChange={c => setV({ bullSpikeColor: c })} />
                      <ColorField label="Bear spike bar" value={v.bearSpikeColor || '#e040fb'} onChange={c => setV({ bearSpikeColor: c })} />
                    </Stack>
                  </>
                );
              })()}
            </Section>

            <Section title="Bollinger Bands (BB)"
              subtitle="A volatility band around a moving average. Four variants: Traditional = SMA of close ± σ (classic). Liner = EMA of the CLH candle's close ± σ (smoother, less lag on trends). Custom Liner = middle is EMA of CLH close, but the upper band is built from the candle bodies' TOPS and the lower from their BOTTOMS (± σ each side) — an asymmetric band that hugs resistance above and support below. Custom Liner Weighted = the same, volume-weighted (VWMA + volume-weighted σ). The Liner variants always compute on CLH candles regardless of the displayed candle type. Toggle the overlay via the BB chip.">
              {(() => {
                const b = (draft as any).bollinger || {};
                const setB = (p: any) => setDraft(d => ({ ...d, bollinger: { ...(d as any).bollinger, ...p } } as any));
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 320 }}>
                        <InputLabel>Variant</InputLabel>
                        <Select label="Variant" value={['traditional', 'liner', 'customLiner', 'customLinerWeighted'].includes(b.variant) ? b.variant : 'traditional'}
                          onChange={e => setB({ variant: e.target.value })}>
                          <MenuItem value="traditional">Traditional (SMA of close ± σ)</MenuItem>
                          <MenuItem value="liner">Liner (EMA of CLH close ± σ)</MenuItem>
                          <MenuItem value="customLiner">Custom Liner (EMA of body top/bottom ± σ)</MenuItem>
                          <MenuItem value="customLinerWeighted">Custom Liner Weighted (VWMA + vol-weighted σ)</MenuItem>
                        </Select>
                      </FormControl>
                      <PeriodSlider label="Period" value={b.period ?? 20} min={2} max={200} onChange={n => setB({ period: n })} />
                      <PeriodSlider label="Std-dev multiplier" value={b.stdMult ?? 2} min={0.5} max={4} step={0.1} onChange={n => setB({ stdMult: n })} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Line display</InputLabel>
                        <Select label="Line display" value={b.lineMode === 'staircase' ? 'staircase' : 'line'}
                          onChange={e => setB({ lineMode: e.target.value })}>
                          <MenuItem value="line">Line (straight)</MenuItem>
                          <MenuItem value="staircase">Staircase (stepped)</MenuItem>
                        </Select>
                      </FormControl>
                      <LineWidthSlider label="Line width" value={b.lineWidth ?? 1} onChange={n => setB({ lineWidth: n })} />
                      <FormControlLabel control={<Switch checked={b.showMiddle !== false} onChange={e => setB({ showMiddle: e.target.checked })} />} label="Show middle band" />
                      <FormControlLabel control={<Switch checked={b.fillEnabled !== false} onChange={e => setB({ fillEnabled: e.target.checked })} />} label="Fill inside band" />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="Band (upper/lower)" value={b.bandColor || '#2962ff'} onChange={c => setB({ bandColor: c })} />
                      <ColorField label="Middle" value={b.middleColor || '#ff6d00'} onChange={c => setB({ middleColor: c })} />
                      <ColorField label="Fill" value={b.fillColor || '#2962ff'} onChange={c => setB({ fillColor: c })} />
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Fill opacity" value={b.fillOpacity ?? 0.08} onChange={n => setB({ fillOpacity: n })} />
                      </Box>
                    </Stack>
                  </>
                );
              })()}
            </Section>

            <Section title="AV_Band — Anchored Volume Band"
              subtitle="From WVol spike bars it holds a High/Low zone; an Effective Line picks the zone's upper / lower / mid edge by this candle's chart-TF OverView score (>20 → high, <-20 → low, else mid). Two HMAs (fast/slow) of that line form a band on the price pane — bullish when the fast HMA ≥ slow HMA. Toggle it via the AV_Band chip.">
              {(() => {
                const a = (draft as any).avBand || {};
                const setA = (p: any) => setDraft(d => ({ ...d, avBand: { ...(d as any).avBand, ...p } } as any));
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <PeriodSlider label="Fast HMA period" value={a.fastPeriod ?? 14} min={2} max={200} onChange={n => setA({ fastPeriod: n })} />
                      <PeriodSlider label="Slow HMA period" value={a.slowPeriod ?? 100} min={5} max={400} onChange={n => setA({ slowPeriod: n })} />
                      <LineWidthSlider label="HMA line width" value={a.lineWidth ?? 2} onChange={n => setA({ lineWidth: n })} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 360 }}>
                        <InputLabel>Effective Line rule</InputLabel>
                        <Select label="Effective Line rule" value={a.effectiveMode === 'logic2' ? 'logic2' : 'logic1'}
                          onChange={e => setA({ effectiveMode: e.target.value })}>
                          <MenuItem value="logic1">Logic 1 — score &gt; 20 → Zone HIGH, &lt; -20 → Zone LOW, else mid</MenuItem>
                          <MenuItem value="logic2">Logic 2 — score &gt; 20 → Zone LOW, &lt; -20 → Zone HIGH, else mid</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Line display</InputLabel>
                        <Select label="Line display" value={a.lineMode === 'line' ? 'line' : 'staircase'}
                          onChange={e => setA({ lineMode: e.target.value })}>
                          <MenuItem value="staircase">Staircase (stepped)</MenuItem>
                          <MenuItem value="line">Line (straight)</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControlLabel control={<Switch checked={a.showFast !== false} onChange={e => setA({ showFast: e.target.checked })} />} label="Show Fast HMA" />
                      <FormControlLabel control={<Switch checked={a.showSlow !== false} onChange={e => setA({ showSlow: e.target.checked })} />} label="Show Slow HMA" />
                      <FormControlLabel control={<Switch checked={a.showBand !== false} onChange={e => setA({ showBand: e.target.checked })} />} label="Show Band (Fast↔Slow)" />
                      <FormControlLabel control={<Switch checked={!!a.showEffective} onChange={e => setA({ showEffective: e.target.checked })} />} label="Show Effective Line" />
                      <LineWidthSlider label="Effective Line width" value={a.effectiveWidth ?? 1} onChange={n => setA({ effectiveWidth: n })} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <ColorField label="Fast HMA" value={a.fastColor || '#26a69a'} onChange={c => setA({ fastColor: c })} />
                      {/* No Slow HMA picker: its colour is derived per bar from the linear-regression
                          close vs the slow HMA and the slow HMA's slope (green agree-up, red
                          agree-down, yellow otherwise). A picker would only break that mapping. */}
                      <ColorField label="Effective Line" value={a.effectiveColor || '#90a4ae'} onChange={c => setA({ effectiveColor: c })} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="Bull band (fast ≥ slow)" value={a.bandBullColor || '#26a69a'} onChange={c => setA({ bandBullColor: c })} />
                      <ColorField label="Bear band (fast < slow)" value={a.bandBearColor || '#ef5350'} onChange={c => setA({ bandBearColor: c })} />
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Band opacity" value={a.bandOpacity ?? 0.25} onChange={n => setA({ bandOpacity: n })} />
                      </Box>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, mb: 0.5, fontWeight: 700 }}>
                      AV_Trend Band — support/resistance channel off the Slow HMA
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                      A fresh Regular close ABOVE the Slow HMA → Long: the lower edge marks the lowest low since the last time a candle touched the Slow HMA. A fresh close BELOW → Short: the upper edge marks the highest high since the last touch. The band fills green while Long, red while Short.
                    </Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <FormControlLabel control={<Switch checked={!!a.showTrendBand} onChange={e => setA({ showTrendBand: e.target.checked })} />} label="Show AV_Trend Band" />
                      <ColorField label="Long (support)" value={a.trendBullColor || '#26a69a'} onChange={c => setA({ trendBullColor: c })} />
                      <ColorField label="Short (resistance)" value={a.trendBearColor || '#ef5350'} onChange={c => setA({ trendBearColor: c })} />
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Trend fill opacity" value={a.trendOpacity ?? 0.15} onChange={n => setA({ trendOpacity: n })} />
                      </Box>
                    </Stack>
                  </>
                );
              })()}
            </Section>

            <Section title="Trend — combined score, HMA, zones & divergence"
              subtitle="Chart-TF (thin) + HTF (thick) trend scores (±100), their Combined sum (±200) and a Hull MA of it. Combined is bull above the HMA / bear below; the HMA is bull rising / bear falling / neutral flat; the band between them shows only when BOTH are plotted. The area above/below the zero line is shaded, the pane background is tinted by the Combined's sign (vertical zones), and price↔HMA divergence is auto-detected. Toggle the whole overlay via the 'Trend' button next to OverView / Bkground.">
              {(() => {
                const tr = (draft as any).trend || {};
                const setTr = (patchObj: any) => setDraft(d => ({ ...d, trend: { ...(d as any).trend, ...patchObj } } as any));
                return (
                  <>
                    {paneToggle('trend')}
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControlLabel control={<Switch size="small" checked={tr.showIndividual !== false} onChange={e => setTr({ showIndividual: e.target.checked })} />} label="Individual lines (TF + HTF)" />
                      <FormControlLabel control={<Switch size="small" checked={tr.showCombined !== false} onChange={e => setTr({ showCombined: e.target.checked })} />} label="Combined line (TF + HTF)" />
                      <FormControlLabel control={<Switch size="small" checked={tr.showHma !== false} onChange={e => setTr({ showHma: e.target.checked })} />} label="HMA line" />
                    </Stack>

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Individual lines</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <ColorField label="Line color" value={tr.color || '#42a5f5'} onChange={v => setTr({ color: v })} />
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Line opacity" value={tr.opacity ?? 0.9} onChange={v => setTr({ opacity: v })} />
                      </Box>
                      <PeriodSlider label="TF width (thin)" value={tr.tfLineWidth ?? 1} min={1} max={6}
                        onChange={n => setTr({ tfLineWidth: n })} />
                      <PeriodSlider label="HTF width (thick)" value={tr.htfLineWidth ?? 3} min={1} max={6}
                        onChange={n => setTr({ htfLineWidth: n })} />
                    </Stack>

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Bull / Bear / Neutral colors (shared across the indicator)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <ColorField label="Bullish" value={tr.bullColor || '#26a69a'} onChange={v => setTr({ bullColor: v })} />
                      <ColorField label="Bearish" value={tr.bearColor || '#ef5350'} onChange={v => setTr({ bearColor: v })} />
                      <ColorField label="Neutral (HMA flat)" value={tr.neutralColor || '#b0bec5'} onChange={v => setTr({ neutralColor: v })} />
                    </Stack>

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Line widths (colors are dynamic: Combined = bull above HMA / bear below; HMA = bull rising / bear falling / neutral flat)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <PeriodSlider label="Combined width" value={tr.combinedLineWidth ?? 2} min={1} max={6}
                        onChange={n => setTr({ combinedLineWidth: n })} />
                      <PeriodSlider label="HMA width" value={tr.hmaLineWidth ?? 4} min={1} max={6}
                        onChange={n => setTr({ hmaLineWidth: n })} />
                      <PeriodSlider label="HMA period (bars)" value={tr.hmaPeriod ?? 20} min={2} max={200}
                        onChange={n => setTr({ hmaPeriod: n })} />
                    </Stack>

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Fills &amp; zones (use the Bull / Bear colors above)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <Box sx={{ width: 210 }}>
                        <OpacitySlider label="Combined↔HMA band opacity" value={tr.bandOpacity ?? 0.18} onChange={v => setTr({ bandOpacity: v })} />
                      </Box>
                      <Box sx={{ width: 210 }}>
                        <OpacitySlider label="Above/below-zero area opacity" value={tr.zeroAreaOpacity ?? 0.15} onChange={v => setTr({ zeroAreaOpacity: v })} />
                      </Box>
                      <Box sx={{ width: 210 }}>
                        <OpacitySlider label="Vertical zones opacity" value={tr.zoneOpacity ?? 0.06} onChange={v => setTr({ zoneOpacity: v })} />
                      </Box>
                    </Stack>

                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      <FormControlLabel control={<Switch size="small" checked={tr.showDivergence !== false} onChange={e => setTr({ showDivergence: e.target.checked })} />}
                        label={<Typography variant="caption">Show divergence markers (regular ▲▼ + hidden ●, price vs HMA)</Typography>} />
                    </Stack>

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Line smoothing (applied to TF / HTF / Combined)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <FormControl size="small" sx={{ width: 200 }}>
                        <InputLabel>Smoothing</InputLabel>
                        <Select label="Smoothing" value={tr.smoothing || 'linreg'}
                          onChange={e => setTr({ smoothing: e.target.value })}>
                          <MenuItem value="linreg">Linear Regression (LSMA)</MenuItem>
                          <MenuItem value="hull">Hull MA</MenuItem>
                          <MenuItem value="ema">EMA</MenuItem>
                          <MenuItem value="none">None (raw steps)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField size="small" type="number" label="Smoothing period (bars)" value={tr.smoothingPeriod ?? 14}
                        onChange={ev => { const n = Math.max(1, Math.min(200, parseInt(ev.target.value, 10) || 14)); setTr({ smoothingPeriod: n }); }}
                        inputProps={{ min: 1, max: 200 }} sx={{ width: 180 }} />
                    </Stack>
                  </>
                );
              })()}
            </Section>

            <Section title="OverView — trend meter"
              subtitle="The on-chart OverView dashboard rolls 14 indicators (EMAs, AvgCloud, CPR, C-POC, H-POC, RelStr, ChgN%, MACD, JHM, WR%, BO_D, BO_W, Vol, AV_Band) into a -100..+100 score per timeframe, shown as a speedometer. Each constituent can be toggled on/off (and weighted); the toggles apply everywhere the score is used — chart, Scan, Backtest and Autotrade. Zone colors + thresholds drive the gauge and the Bkground tint.">
              {(() => {
                const ov = (draft as any).overview || {};
                const setOv = (patchObj: any) => setDraft(d => ({ ...d, overview: { ...(d as any).overview, ...patchObj } } as any));
                const setZone = (k: string, v: string) => setOv({ zoneColors: { ...(ov.zoneColors || {}), [k]: v } });
                const setWeight = (k: string, v: number) => setOv({ weights: { ...(ov.weights || {}), [k]: v } });
                const W: Array<[string, string]> = [['ema', 'EMAs'], ['avgCloud', 'AvgCloud'], ['cpr', 'CPR'], ['cpoc', 'C-POC'], ['hpoc', 'H-POC'], ['relStrength', 'RelStr'], ['chgN', 'ChgN%'], ['macd', 'MACD'], ['jhm', 'JHM'], ['wrPct', 'WR%'], ['bolevD', 'BO_D'], ['bolevW', 'BO_W'], ['vol', 'Vol'], ['avBand', 'AV_Band']];
                // A contributor is ON when its weight is > 0; the toggle sets 1/0.
                const isOn = (k: string) => ((ov.weights?.[k] ?? 1) > 0);
                const Z: Array<[string, string, string]> = [
                  ['strongBuy', 'Strong Buy', '#00c853'], ['probableBuy', 'Probable Buy', '#66bb6a'],
                  ['neutral', 'Neutral', '#9e9e9e'], ['probableShort', 'Probable Short', '#ffa726'], ['strongShort', 'Strong Short', '#e53935'],
                ];
                return (
                  <>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Zone colors</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      {Z.map(([k, label, def]) => (
                        <ColorField key={k} label={label} value={ov.zoneColors?.[k] || def} onChange={v => setZone(k, v)} />
                      ))}
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Trend shapes</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <ColorField label="Up ▲" value={ov.upColor || '#26a69a'} onChange={v => setOv({ upColor: v })} />
                      <ColorField label="Down ▼" value={ov.downColor || '#ef5350'} onChange={v => setOv({ downColor: v })} />
                      <ColorField label="Neutral ◆" value={ov.neutralColor || '#fbc02d'} onChange={v => setOv({ neutralColor: v })} />
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Gauge opacity" value={ov.gaugeOpacity ?? 0.85} onChange={v => setOv({ gaugeOpacity: v })} />
                      </Box>
                      <Box sx={{ width: 180 }}>
                        <OpacitySlider label="Bkground tint opacity" value={ov.bgTintOpacity ?? 0.10} onChange={v => setOv({ bgTintOpacity: v })} />
                      </Box>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Zone thresholds (|score|)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      {/* F40 — thresholds as sliders. 0-100 OverView score range. */}
                      <PeriodSlider label="Strong ≥"   value={ov.strongThreshold   ?? 60} min={20} max={100}
                        onChange={n => setOv({ strongThreshold: n })} />
                      <PeriodSlider label="Probable ≥" value={ov.probableThreshold ?? 20} min={5}  max={80}
                        onChange={n => setOv({ probableThreshold: n })} />
                    </Stack>
                    {/* R120 — per-constituent on/off toggles. A constituent that's
                       OFF contributes nothing to the score everywhere (chart gauge,
                       RockBand colours, Scan, Backtest, Autotrade). Toggling sets
                       its weight to 1 (on) or 0 (off); the numeric weights below
                       still allow fine-tuning the relative pull of the ON ones. */}
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Contributors (on / off)</Typography>
                    <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, mb: 1 }}>
                      {W.map(([k, label]) => (
                        <FormControlLabel key={k} sx={{ mr: 0.5 }}
                          control={<Switch size="small" checked={isOn(k)} onChange={e => setWeight(k, e.target.checked ? 1 : 0)} />}
                          label={<Typography variant="caption">{label}</Typography>} />
                      ))}
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Indicator weights (relative pull when ON)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      {W.map(([k, label]) => (
                        <TextField key={k} size="small" type="number" label={label} value={ov.weights?.[k] ?? 1}
                          onChange={ev => { const n = Math.max(0, Math.min(10, Number(ev.target.value))); setWeight(k, Number.isFinite(n) ? n : 1); }}
                          inputProps={{ min: 0, max: 10, step: 0.5 }} sx={{ width: 92 }} />
                      ))}
                    </Stack>
                  </>
                );
              })()}
            </Section>

            <Section title="Bkground — regime background tint"
              subtitle="Tints each candle's background to show the trend regime as vertical zones on the price chart. Toggle it via the 'Bkground' button next to OverView. Choose how the background colour is computed below.">
              {(() => {
                const bg = (draft as any).bkground || {};
                const setBg = (patchObj: any) => setDraft(d => ({ ...d, bkground: { ...(d as any).bkground, ...patchObj } } as any));
                const method = bg.method === 'htf' ? 'htf'
                  : bg.method === 'clhMid' ? 'clhMid'
                    : bg.method === 'stack' ? 'stack' : 'ctf';
                const stackSrc = bg.srcCandle || 'CLN';
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <FormControl size="small" sx={{ width: 220 }}>
                        <InputLabel>Compute by</InputLabel>
                        <Select label="Compute by" value={method}
                          onChange={e => setBg({ method: e.target.value })}>
                          <MenuItem value="ctf">Overview_CTF</MenuItem>
                          <MenuItem value="htf">Overview_HTF</MenuItem>
                          <MenuItem value="clhMid">CLH midline (HTF)</MenuItem>
                          <MenuItem value="stack">Candle / body / HTF stack</MenuItem>
                        </Select>
                      </FormControl>
                      {method === 'stack' && (
                        <FormControl size="small" sx={{ width: 160 }}>
                          <InputLabel>Candle type</InputLabel>
                          <Select label="Candle type" value={stackSrc}
                            onChange={e => setBg({ srcCandle: e.target.value })}>
                            <MenuItem value="Regular">Reg</MenuItem>
                            <MenuItem value="HA">HA</MenuItem>
                            <MenuItem value="CLH">CLH</MenuItem>
                            <MenuItem value="CLN">CLN</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 460 }}>
                        {method === 'stack'
                          ? (stackSrc === 'Regular'
                            ? 'Candle / body / HTF stack (Reg) — bullish where the close is above the WHOLE higher-timeframe body, bearish where it is below it, neutral while it is inside. Reg has no smoothing, so the middle rung is dropped rather than compared against itself (it would be the same number and could never fire). The band is the HTF body CONTAINING each candle — the one drawn on the chart — so it reads as context, not as something that was knowable at the time.'
                            : `Candle / body / HTF stack (${stackSrc}) — three levels have to agree, in order: bullish when the real close is above the ${stackSrc} body's close AND that close is above the WHOLE higher-timeframe ${stackSrc} body; bearish for the mirror; neutral otherwise. Neutral is the point — while price works inside the higher-timeframe body there is no regime to report. The first rung always uses the REAL close, even on an HA or CLH chart, so it is never a smoothed number compared against a smoothed body. The band is the HTF body CONTAINING each candle — the one drawn on the chart — so it reads as context, not as something that was knowable at the time.`)
                          : method === 'htf'
                            ? 'Overview_HTF — paints each candle with the RockBand zone colour (5 OverView zones) of the Higher-Timeframe bar that contains it, mapped by the candle’s start time. Mirrors the HTF trend; unaffected by the current chart’s strategy / candle type / other indicators. Colours follow the OverView zone palette + thresholds above.'
                            : method === 'clhMid'
                              ? 'CLH midline (HTF) — the slope of the CLH Band’s higher-timeframe middle line: bullish where it is FALLING, bearish where it is RISING, exactly as that line colours itself. The CLH Band indicator does not have to be switched on. Each candle reads the last HTF bar that CLOSED before it, so nothing here depends on a bar that finishes in that candle’s future. Colours below.'
                              : 'Overview_CTF — sign of the combined (chart-TF + HTF) trend score: bullish when > 0, bearish when ≤ 0. Uses the same smoothing as the Trend indicator so zones line up with the Trend Combined line’s zero crossings. Colours below.'}
                      </Typography>
                    </Stack>
                    {method !== 'htf' && (
                      <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                        <ColorField label={method === 'clhMid' ? 'Bullish (midline falling)' : method === 'stack' ? 'Bullish (stacked up)' : 'Bullish (score > 0)'} value={bg.bullColor || '#26a69a'} onChange={v => setBg({ bullColor: v })} />
                        <ColorField label={method === 'clhMid' ? 'Bearish (midline rising)' : method === 'stack' ? 'Bearish (stacked down)' : 'Bearish (score ≤ 0)'} value={bg.bearColor || '#ef5350'} onChange={v => setBg({ bearColor: v })} />
                        {method === 'stack' && (
                          <ColorField label="Neutral (inside the band)" value={bg.neutralColor || '#90a4ae'} onChange={v => setBg({ neutralColor: v })} />
                        )}
                        <Box sx={{ width: 220 }}>
                          <OpacitySlider label="Background opacity" value={bg.opacity ?? 0.10} onChange={v => setBg({ opacity: v })} />
                        </Box>
                      </Stack>
                    )}
                    {method === 'htf' && (
                      <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                        <Box sx={{ width: 220 }}>
                          <OpacitySlider label="Background opacity" value={bg.opacity ?? 0.10} onChange={v => setBg({ opacity: v })} />
                        </Box>
                      </Stack>
                    )}
                  </>
                );
              })()}
            </Section>

            <Section title="DbCrZone — Debit/Credit option-market regime"
              subtitle="Vertical background zones on the UNDERLYING showing the option-market regime: DEBIT (trending / premium-expansion — negative dealer gamma with rising straddle/IV; a trending move is likely) vs CREDIT (range / premium-decay — favours selling premium). Captured live from GEX + straddle + IV and saved as history, so the zones still render months later after the contracts expire. Toggle via the 'DbCrZone' button next to Bkground.">
              {(() => {
                const dz = (draft as any).dbcr || {};
                const setDz = (patchObj: any) => setDraft(d => ({ ...d, dbcr: { ...(d as any).dbcr, ...patchObj } } as any));
                return (
                  <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                    <ColorField label="Debit (trending)" value={dz.debitColor || '#ef5350'} onChange={v => setDz({ debitColor: v })} />
                    <ColorField label="Credit (range)" value={dz.creditColor || '#26a69a'} onChange={v => setDz({ creditColor: v })} />
                    <Box sx={{ width: 220 }}>
                      <OpacitySlider label="Zone opacity" value={dz.opacity ?? 0.12} onChange={v => setDz({ opacity: v })} />
                    </Box>
                  </Stack>
                );
              })()}
            </Section>

            <Section title="Bhairav — strategy simulation"
              subtitle="The Bhairav STRATEGY chip's inputs: which candle transform drives the BUY/SELL/SHORT/COVER state machine, and the notional the bottom-left simulation badge trades with. 'Follow chart' uses the chart's selected candle type (HA/CLH; any other display type computes on CLH).">
              {(() => {
                const bh = (draft as any).bhairav || {};
                const setBh = (patchObj: any) => setDraft(d => ({ ...d, bhairav: { ...(d as any).bhairav, ...patchObj } } as any));
                return (
                  <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <InputLabel>Candle type</InputLabel>
                      <Select label="Candle type" value={bh.candleType || 'FOLLOW'}
                        onChange={e => setBh({ candleType: e.target.value })}>
                        <MenuItem value="FOLLOW">Follow chart (HA/CLH)</MenuItem>
                        <MenuItem value="HA">Heikin-Ashi</MenuItem>
                        <MenuItem value="CLH">CLH (smoothed)</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" type="number" label="Invested amount (₹)" sx={{ width: 170 }}
                      value={bh.investInr ?? 100000}
                      onChange={e => setBh({ investInr: Math.max(1000, Number(e.target.value) || 100000) })}
                      inputProps={{ min: 1000, step: 10000 }} />
                  </Stack>
                );
              })()}
            </Section>

            <Section title="OM_Simple — CLN colour-flip strategy"
              subtitle="OM_Simple fires BUY the bar the CLN colour turns red→green and SHORT the bar it turns green→red. It has NO exits: a BUY is closed only by the next SHORT, so the stop and target are yours to place. This length is what the STRATEGY evaluates; the CLN section above sets the length the chart DRAWS. When they differ the chart shows a warning, because markers would then sit on colour changes you cannot see.">
              {(() => {
                const om = (draft as any).omSimple || {};
                const setOm = (patchObj: any) => setDraft(d => ({ ...d, omSimple: { ...(d as any).omSimple, ...patchObj } } as any));
                const drawn = draft.candle.clnLength ?? 14;
                const len = om.clnLength ?? 50;
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <TextField size="small" type="number" label="Signal regression length" sx={{ width: 210 }}
                        value={len}
                        onChange={e => setOm({ clnLength: Math.max(2, Math.min(200, Number(e.target.value) || 50)) })}
                        inputProps={{ min: 2, max: 200 }}
                        helperText="2–200; default 50" />
                      <FormControlLabel
                        control={<Switch size="small" checked={om.suppressThinVolume === true}
                          onChange={e => setOm({ suppressThinVolume: e.target.checked })} />}
                        label={<Typography variant="caption">Skip flips printed on thin volume</Typography>}
                      />
                      {len !== drawn && (
                        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                          Chart draws CLN at {drawn} — markers will not line up with the colours until these match.
                        </Typography>
                      )}
                    </Stack>

                    {/* W / C marks. Kept visually separate from the signal controls
                        above because they are a different KIND of thing: annotations
                        that answer "keep holding?", not trades. */}
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={<Switch size="small" checked={om.showWatch !== false}
                          onChange={e => setOm({ showWatch: e.target.checked })} />}
                        label={<Typography variant="caption">W — leg weakening</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch size="small" checked={om.showContinuation !== false}
                          onChange={e => setOm({ showContinuation: e.target.checked })} />}
                        label={<Typography variant="caption">C — leg resuming</Typography>}
                      />
                      <TextField size="small" type="number" label="W threshold" sx={{ width: 150 }}
                        value={om.watchThreshold ?? 45}
                        onChange={e => setOm({ watchThreshold: Math.max(0, Math.min(100, Number(e.target.value) || 45)) })}
                        inputProps={{ min: 0, max: 100 }}
                        helperText="default 45 — ~3.5 per 100 bars" />
                      <TextField size="small" type="number" label="C threshold" sx={{ width: 150 }}
                        value={om.continuationThreshold ?? 55}
                        onChange={e => setOm({ continuationThreshold: Math.max(0, Math.min(100, Number(e.target.value) || 55)) })}
                        inputProps={{ min: 0, max: 100 }}
                        helperText="default 55 — ~5 per 100 bars" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      <b>W</b> warns that the leg you are in is weakening and is coloured for the direction a flip would go —
                      held long, it is a red W above the bar. <b>C</b> says the leg is resuming and the higher timeframe agrees,
                      so it is coloured for the direction you already hold. <b>Neither is a trade</b>: they raise no alert, write
                      no scan row and do not change what OM_Simple signals. Measured over 48 symbols on 4H bars, a flip follows
                      within 10 bars <b>70%</b> of the time after a W against a <b>45%</b> base rate. The thresholds are not the
                      TradingView reference's 55 and 70 — on this data those sit at the 98th and 96th percentile, so copying them
                      would draw almost nothing. A C is hidden entirely when the interval has no higher timeframe to check.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Why 50 and not the chart's 14: measured on 10 majors, Mar 2024 – Sep 2026, 7H bars, 0.20% round-trip
                      cost — length 14 fired 22.5 times a month per symbol for −0.125% a trade (profitable on 1 of 10 symbols),
                      length 50 fired 11.3 times for +0.045% (7 of 10). Treat it as roughly break-even: a signal to confirm by
                      eye, not an edge to automate. Skipping thin-volume flips tested slightly better again (+0.165%, PF 1.08 on
                      4H) but leaves visible colour changes unmarked, which is why it is off by default.
                    </Typography>
                  </>
                );
              })()}
            </Section>

            <Section title="CLH Band — where the next CLH body breaks out"
              subtitle={'The real price the NEXT candle would have to close at for the next CLH body to clear this body top (upper line) or break its bottom (lower line). Solved in closed form from the CLH transform itself, so it is exact and identical whatever candle type the chart is showing. READ THE LINES AS ROLE-SWITCHING: the upper line sits BELOW spot on about half of all bars, because when CLH is already rising the next body clears the old top even at flat price — there it marks where the up-move would fail rather than a breakout. The optional flip line is the one that is always a genuine trend change. Levels are often far away (a quarter of bars beyond ~10 ATR) because CLH moves only 2.79% of a price move; the band is drawn so it never squashes your candles, and the right-edge labels stay readable when a line is off-screen.'}>
              {(() => {
                const cb = (draft as any).clhBand || {};
                return (
                  <>
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={cb.showTf !== false}
                        onChange={e => patch('clhBand', { showTf: e.target.checked })} />}
                      label="Show for this timeframe" />
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={cb.showHtf === true}
                        onChange={e => patch('clhBand', { showHtf: e.target.checked })} />}
                      label="Show for the mapped higher timeframe (dimmer; style below)" />
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={cb.showBandLines !== false}
                        onChange={e => patch('clhBand', { showBandLines: e.target.checked })} />}
                      label="Show the two band edges (upper + lower)" />
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={cb.showFlip === true}
                        onChange={e => patch('clhBand', { showFlip: e.target.checked })} />}
                      label="Show the middle line (the colour-flip level, coloured by its own slope)" />
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={cb.showFill !== false}
                        onChange={e => patch('clhBand', { showFill: e.target.checked })} />}
                      label="Fill between the two lines (tinted by the CLH body direction)" />
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={cb.showLabels !== false}
                        onChange={e => patch('clhBand', { showLabels: e.target.checked })} />}
                      label="Price-scale labels (keeps an off-screen level readable)" />
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mt: 1 }}>
                      <ColorField label="Upper (long break)" value={cb.upperColor || '#26a69a'} onChange={(v: string) => patch('clhBand', { upperColor: v })} />
                      <ColorField label="Lower (short break)" value={cb.lowerColor || '#ef5350'} onChange={(v: string) => patch('clhBand', { lowerColor: v })} />
                      <ColorField label="Middle — falling (bullish)" value={cb.flipFallColor || cb.flipAboveColor || '#26a69a'} onChange={(v: string) => patch('clhBand', { flipFallColor: v })} />
                      <ColorField label="Middle — rising (bearish)" value={cb.flipRiseColor || cb.flipBelowColor || '#ef5350'} onChange={(v: string) => patch('clhBand', { flipRiseColor: v })} />
                      <ColorField label="Fill — CLH bullish" value={cb.fillBullColor || '#26a69a'} onChange={(v: string) => patch('clhBand', { fillBullColor: v })} />
                      <ColorField label="Fill — CLH bearish" value={cb.fillBearColor || '#ef5350'} onChange={(v: string) => patch('clhBand', { fillBearColor: v })} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mt: 1 }}>
                      <TextField size="small" type="number" label="Fill opacity" sx={{ width: 150 }}
                        inputProps={{ step: 0.05, min: 0, max: 0.6 }}
                        value={cb.fillOpacity ?? 0.10} onChange={e => patch('clhBand', { fillOpacity: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Line width — this TF" sx={{ width: 160 }}
                        inputProps={{ step: 1, min: 1, max: 4 }}
                        value={cb.lineWidth ?? 2} onChange={e => patch('clhBand', { lineWidth: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Line width — HTF" sx={{ width: 150 }}
                        inputProps={{ step: 1, min: 1, max: 4 }}
                        value={cb.lineWidthHtf ?? 2} onChange={e => patch('clhBand', { lineWidthHtf: Number(e.target.value) })} />
                      <TextField size="small" select label="Line style — this TF" sx={{ width: 170 }}
                        SelectProps={{ native: true }}
                        value={cb.lineStyle ?? 0} onChange={e => patch('clhBand', { lineStyle: Number(e.target.value) })}>
                        <option value={0}>Solid</option>
                        <option value={1}>Dotted</option>
                        <option value={2}>Dashed</option>
                      </TextField>
                      <TextField size="small" select label="Line style — HTF" sx={{ width: 160 }}
                        SelectProps={{ native: true }}
                        value={cb.lineStyleHtf ?? 2} onChange={e => patch('clhBand', { lineStyleHtf: Number(e.target.value) })}>
                        <option value={0}>Solid</option>
                        <option value={1}>Dotted</option>
                        <option value={2}>Dashed</option>
                      </TextField>
                      <TextField size="small" type="number" label="HTF dimming" sx={{ width: 150 }}
                        inputProps={{ step: 0.05, min: 0.1, max: 1 }}
                        value={cb.htfOpacityScale ?? 0.55} onChange={e => patch('clhBand', { htfOpacityScale: Number(e.target.value) })} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      The level shown against a candle is the one that governs the candle AFTER it — it becomes known only at that
                      candle&rsquo;s close, so it is drawn one bar to the right and can never be read as available earlier. The higher-timeframe
                      lines use the last HTF candle that has fully CLOSED, never the one currently forming, which is what lets you act on a
                      lower-timeframe close while knowing where the higher timeframe flips. Not to be confused with BO_D / BO_W, which are also
                      CLH breakout levels but a different quantity entirely (the highest high and lowest low of the last N completed CLH candles).
                      The edges and the middle line toggle separately: because the two edges sit about 36&times; the CLH body apart, showing the
                      middle line alone is usually the cleanest read. The middle line is coloured bar by bar by where price closed relative to
                      it &mdash; green above, red below. Nothing here places an order.
                    </Typography>
                  </>
                );
              })()}
            </Section>

            <Section title="Vajra — oversold buy, gated by the higher timeframe"
              subtitle="Vajra takes the BBR×Wave oversold buy ONLY when the mapped higher timeframe has extended at least N bars in its current direction, and exits on BBR reaching the midline, a 2.5×ATR stop, or 20 bars. At an oversold entry that direction is almost always DOWN (1,019 of 1,025 occurrences), so in plain terms: the higher timeframe is falling too and has been for a while. Measured on 47 crypto symbols and 487,000 four-hour bars (2019-2026), net of 0.20% round-trip cost: ungated the rule returns +0.10% a trade at 53.7% win; gated it returns +0.93% at 56.9% win, about 3.3 signals per symbol per year. It beat all 4,000 random selections of the same size, won 4 of 4 walk-forward folds, improved 36 of 47 symbols and beat the ungated rule in seven of eight years. Chart marks only — nothing here places an order.">
              {(() => {
                const v = (draft as any).vajra || {};
                return (
                  <>
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={<Switch size="small" checked={v.requireTurn !== false}
                        onChange={e => patch('vajra', { requireTurn: e.target.checked })} />}
                      label="Require BBR to be turning up" />
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mt: 1 }}>
                      <TextField size="small" type="number" label="BBR oversold" sx={{ width: 150 }}
                        value={v.bbOs ?? -100} onChange={e => patch('vajra', { bbOs: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Wave oversold" sx={{ width: 150 }}
                        value={v.wvOs ?? -60} onChange={e => patch('vajra', { wvOs: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Min HTF run (0 = off)" sx={{ width: 170 }}
                        inputProps={{ step: 1, min: 0 }}
                        value={v.minHtfRun ?? 2} onChange={e => patch('vajra', { minHtfRun: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Exit at BBR" sx={{ width: 150 }}
                        value={v.exitLevel ?? 0} onChange={e => patch('vajra', { exitLevel: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Stop (× ATR)" sx={{ width: 150 }}
                        inputProps={{ step: 0.5, min: 0 }}
                        value={v.stopAtr ?? 2.5} onChange={e => patch('vajra', { stopAtr: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Max bars held" sx={{ width: 150 }}
                        value={v.maxHold ?? 20} onChange={e => patch('vajra', { maxHold: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Risk per trade %" sx={{ width: 150 }}
                        inputProps={{ step: 0.25, min: 0 }}
                        value={v.riskPct ?? 1} onChange={e => patch('vajra', { riskPct: Number(e.target.value) })} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Raising the minimum run raises quality and cuts frequency, smoothly: 0/1/2/3/4 give +0.10% / +0.19% /
                      +0.93% / +1.85% / +2.84% a trade on 2,780 / 1,928 / 1,054 / 496 / 242 signals. The default is 2 because
                      that is the value that was pre-registered and passed multiple-testing control — 3 and 4 look better but
                      were not in the tested family, so treat them as your choice rather than as a measured claim. The stop
                      stays at 2.5×ATR: the wider 4×ATR stop that wins on DAILY bars does not carry over here, because a held
                      trade blocks the next signal, so a wider stop costs entries (swept in the engine: 1.5→+0.54%, 2.5→+0.93%,
                      4.0→+0.61%). Three things that did NOT work and are not offered:
                      gating on the higher-timeframe BAND being green — it measured +1.48% and was look-ahead, because that
                      band stamps each bar with the higher-timeframe body containing it, which closes in that bar's future
                      (made causal it is worth +0.12% against +0.10%, i.e. nothing); requiring the higher timeframe's
                      OSCILLATOR to be oversold as well (+0.45%, negative in three of eight years); and a plain
                      close-above-EMA50 trend filter (−0.20%, worse than no filter at all). Risk per trade sizes the position
                      between entry and stop; it is guidance on the chart, not an order.
                    </Typography>
                  </>
                );
              })()}
            </Section>

            <Section title="CLN Break — breakout of the CLN regression bodies"
              subtitle="CLN_Break puts two levels on every bar: the HIGHEST of the previous N CLN linear-regression body tops, and the LOWEST of their bottoms. Price touching one takes that side and squares off the other, so it is always in the market with no stop of its own. THE ONE THING TO KNOW BEFORE USING IT: those bodies are a regression, so the channel between the two levels is NARROWER THAN ONE AVERAGE BAR'S RANGE — 0.72×ATR on crypto, 0.78×ATR on RWA and equities, measured over about 650,000 bars. At least one level is breached on 95-96% of bars and BOTH are breached on 9-18% of them. Bars that breach both are resolved pessimistically — the side against the open position is assumed to fill first — and since that reverses the position every time, those bars drive the turnover: the rule flips every 4.3 bars on daily candles and every 3.7 on 4-hour ones. The channel WIDTH is the same at every timeframe; the flip RATE is not, because a faster bar covers more of a fixed-width channel. Cost follows straight from that — at 0.20% a trade on a 24×7 venue, about 17% a year on daily bars, 58% on 8H and 119% on 4H — which is why the buffer below matters more than anything else here. Chart marks only — nothing here places an order.">
              {(() => {
                const cb = (draft as any).clnBreak || {};
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <FormControlLabel
                        control={<Switch size="small" checked={cb.showLevels !== false}
                          onChange={e => patch('clnBreak', { showLevels: e.target.checked })} />}
                        label={<Typography variant="caption">Draw the two level lines</Typography>} />
                      <FormControlLabel
                        control={<Switch size="small" checked={cb.breach === 'close'}
                          onChange={e => patch('clnBreak', { breach: e.target.checked ? 'close' : 'touch' })} />}
                        label={<Typography variant="caption">Breach on CLOSE (off = intrabar touch)</Typography>} />
                      <FormControlLabel
                        control={<Switch size="small" checked={cb.requireLrAgree === true}
                          onChange={e => patch('clnBreak', { requireLrAgree: e.target.checked })} />}
                        label={<Typography variant="caption">Only the side the regression agrees with</Typography>} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <PeriodSlider label="Bodies per level" value={cb.lookback ?? 3} min={1} max={20}
                        onChange={n => patch('clnBreak', { lookback: n })} />
                      <TextField size="small" type="number" label="Buffer (× ATR)" sx={{ width: 150 }}
                        inputProps={{ step: 0.25, min: 0 }}
                        value={cb.bufferAtr ?? 0} onChange={e => patch('clnBreak', { bufferAtr: Number(e.target.value) })} />
                      <TextField size="small" type="number" label="Stop (× ATR, 0 = none)" sx={{ width: 180 }}
                        inputProps={{ step: 0.5, min: 0 }}
                        value={cb.stopAtr ?? 0} onChange={e => patch('clnBreak', { stopAtr: Number(e.target.value) })} />
                      <ColorField label="Long level" value={cb.buyColor || '#43a047'} onChange={v => patch('clnBreak', { buyColor: v })} />
                      <ColorField label="Short level" value={cb.shortColor || '#e53935'} onChange={v => patch('clnBreak', { shortColor: v })} />
                      <LineWidthSlider label="Line width" value={cb.lineWidth ?? 2} onChange={n => patch('clnBreak', { lineWidth: n })} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      What each dial actually does, measured over 163 crypto symbols rather than guessed. BUFFER is the one
                      that matters: 0.5×ATR takes the share of bars breaching both levels from 13.5% down to 1.2% and cuts
                      flips by 47%, because it widens a channel that is otherwise thinner than a single bar — and every
                      ambiguous bar it removes is a reversal that no longer happens. BODIES PER LEVEL does the same thing more
                      bluntly: 10 bodies widen the channel to 2.07×ATR and flip once every 10.4 bars instead of every 4.3.
                      REGRESSION LENGTH is not a dial here: the levels are built from the bodies the chart draws, at the
                      CLN length set under Candle, so a mark can never refer to bodies that are not on screen. The study
                      measured length 14 only. Length goes the other way from how it reads — a LONGER regression makes each body THINNER, so
                      length 50 narrows the channel to 0.43×ATR; it still flips less often (every 5.9 bars) because the smoothed
                      line holds one side of price for longer, but it does not widen anything. BREACH ON CLOSE removes ambiguous
                      bars entirely, since the upper level is always at or above the lower one and a close cannot be on both
                      sides of them. THE STOP ships at 0 because that is how the rule was measured; switching one on makes the
                      system no longer always-in and is a risk choice, not a returns one.
                      Every figure in this card is recomputed by `npm run verify:cln-break`.
                    </Typography>
                  </>
                );
              })()}
            </Section>

            <Section title="Gandiv — higher-timeframe band breakout"
              subtitle="Gandiv goes long when the source candle's close crosses ABOVE the higher-timeframe CLN band, with the stop fixed at the band's lower line; short is the mirror. If the stop is crossed it reverses, and the new stop is the band line from when the stopped trade was opened. The band is the higher-timeframe regression body AS IT WAS KNOWN at each bar's close, so nothing drawn moves after its bar has closed. It is not the CLN context band, which shows each window's final body. Entry and SL lines sit at real prices. Chart marks only: nothing here places an order.">
              {(() => {
                const gd = (draft as any).gandiv || {};
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Source candle</InputLabel>
                        <Select label="Source candle" value={gd.source || 'HA'}
                          onChange={e => patch('gandiv', { source: e.target.value })}>
                          <MenuItem value="Regular">Reg — real closes</MenuItem>
                          <MenuItem value="HA">HA — Heikin-Ashi</MenuItem>
                          <MenuItem value="CLH">CLH — Linreg HA</MenuItem>
                          <MenuItem value="CLN">CLN — regression body</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={<Switch size="small" checked={gd.showBand !== false}
                          onChange={e => patch('gandiv', { showBand: e.target.checked })} />}
                        label={<Typography variant="caption">Band</Typography>} />
                      <FormControlLabel
                        control={<Switch size="small" checked={gd.showEntry !== false}
                          onChange={e => patch('gandiv', { showEntry: e.target.checked })} />}
                        label={<Typography variant="caption">Entry lines</Typography>} />
                      <FormControlLabel
                        control={<Switch size="small" checked={gd.showSl !== false}
                          onChange={e => patch('gandiv', { showSl: e.target.checked })} />}
                        label={<Typography variant="caption">SL lines</Typography>} />
                      <FormControlLabel
                        control={<Switch size="small" checked={gd.showLabels !== false}
                          onChange={e => patch('gandiv', { showLabels: e.target.checked })} />}
                        label={<Typography variant="caption">Price labels</Typography>} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="Band rising" value={gd.bandUpColor || '#26a69a'} onChange={v => patch('gandiv', { bandUpColor: v })} />
                      <ColorField label="Band falling" value={gd.bandDownColor || '#ef5350'} onChange={v => patch('gandiv', { bandDownColor: v })} />
                      <Box sx={{ width: 200 }}>
                        <OpacitySlider label="Band fill" value={gd.bandOpacity ?? 0.12} onChange={v => patch('gandiv', { bandOpacity: v })} />
                      </Box>
                      <ColorField label="Long entry" value={gd.longColor || '#00c853'} onChange={v => patch('gandiv', { longColor: v })} />
                      <ColorField label="Short entry" value={gd.shortColor || '#ff5252'} onChange={v => patch('gandiv', { shortColor: v })} />
                      <ColorField label="Stop" value={gd.slColor || '#ff9800'} onChange={v => patch('gandiv', { slColor: v })} />
                      <LineWidthSlider label="Line width" value={gd.lineWidth ?? 2} onChange={n => patch('gandiv', { lineWidth: n })} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      REGRESSION LENGTH is not a dial here: the band uses the CLN length set under Candle, the same one the
                      chart draws. The higher timeframe is the one mapped on the Strategies page; a chart at the top of that
                      ladder (1W) has none, so Gandiv marks nothing there. The source candle only decides WHEN a cross
                      happened; HA, CLH and CLN prices were never tradeable, so every Entry line is the real close of the
                      bar that decided it. Nothing about this rule has been backtested.
                    </Typography>
                  </>
                );
              })()}
            </Section>

            <Section title="LR_Cross — candle closes across the LR body"
              subtitle="Marks for the LR_Cross strategy chip: ▲ B on the first closed bar whose source close is ABOVE the LR body top, ▼ S on the first one BELOW the body bottom. A cross the other way also exits the open side, marked B ✕S / S ✕L; 're' is a cross the same way while already in. Alerts and Telegram come from the scheduled scan on the Strategies page, which has its OWN source setting. Nothing here places an order.">
              {(() => {
                const lx = (draft as any).lrCross || {};
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Source candle</InputLabel>
                        <Select label="Source candle" value={lx.source === 'HA' ? 'HA' : 'Regular'}
                          onChange={e => patch('lrCross', { source: e.target.value })}>
                          <MenuItem value="Regular">Reg — real closes</MenuItem>
                          <MenuItem value="HA">HA — Heikin-Ashi closes</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      The LR body is the one the LR chip draws, from the real candles, at the LR length set under Candle; there
                      is no separate length. Each bar is judged against its OWN body, on closed bars only. Switch the LR chip on
                      and show the source candle type to see each crossing. Not backtested.
                    </Typography>
                  </>
                );
              })()}
            </Section>

            <Section title="Funding — Delta perpetual funding rate"
              subtitle="Plots the funding rate (% per 8h) for Delta perpetuals in a lower pane, forward-filled onto every candle (works on any timeframe). Green below zero (shorts pay longs); red above zero (longs pay shorts); fetched on demand from Delta. Funding Signals (below) mark contrarian entries/exits ON the funding pane: fade crowded positioning — high +funding (crowded longs) → SHORT ▼, deep −funding (crowded shorts) → LONG ▲ — firing when funding reverts back from an adaptive z-score extreme; ○ on a zero-cross exit. Toggle via the 'Funding' button (Delta symbols only).">
              {(() => {
                const fn = (draft as any).funding || {};
                const setFn = (patchObj: any) => setDraft(d => ({ ...d, funding: { ...(d as any).funding, ...patchObj } } as any));
                return (
                  <>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                      <ColorField label="Positive (longs pay)" value={fn.positiveColor || '#ef5350'} onChange={v => setFn({ positiveColor: v })} />
                      <ColorField label="Negative (shorts pay)" value={fn.negativeColor || '#26a69a'} onChange={v => setFn({ negativeColor: v })} />
                      <LineWidthSlider label="Line width" value={fn.lineWidth ?? 2} onChange={n => setFn({ lineWidth: n })} />
                      <FormControlLabel
                        control={<Switch size="small" checked={fn.pane !== false} onChange={e => setFn({ pane: e.target.checked })} />}
                        label={<Typography variant="caption">Separate lower pane (off = overlay on price)</Typography>}
                      />
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5, fontWeight: 700 }}>Funding Signals (contrarian — bottom pane markers)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <FormControlLabel
                        control={<Switch size="small" checked={fn.signals !== false} onChange={e => setFn({ signals: e.target.checked })} />}
                        label={<Typography variant="caption">Show signals (▲ / ▼ / ○)</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch size="small" checked={fn.showBands !== false} onChange={e => setFn({ showBands: e.target.checked })} />}
                        label={<Typography variant="caption">Show z-score bands</Typography>}
                      />
                      <LineWidthSlider label="Band width" value={fn.bandWidth ?? 2} onChange={n => setFn({ bandWidth: n })} />
                      <PeriodSlider label="Smoothing EMA" value={fn.emaLength ?? 8} min={1} max={50} onChange={n => setFn({ emaLength: n })} />
                      <PeriodSlider label="Lookback (bars)" value={fn.lookback ?? 100} min={20} max={500} onChange={n => setFn({ lookback: n })} />
                      <TextField size="small" type="number" label="Extreme (σ)" value={fn.k ?? 2}
                        onChange={ev => { const v = Math.max(0.5, Math.min(5, Number(ev.target.value))); setFn({ k: Number.isFinite(v) ? v : 2 }); }}
                        inputProps={{ min: 0.5, max: 5, step: 0.5 }} sx={{ width: 110 }} />
                    </Stack>
                  </>
                );
              })()}
            </Section>

            <Section title="BOLev — Daily / Weekly breakout levels"
              subtitle="BOLev_D / BOLev_W project BUY / SHORT breakout levels from the previous N completed Daily / Weekly CLH candles (max-high / min-low), shown on any chart timeframe & candle type. An Open=High candle arms a BUY lookout; price breaking the level activates a BUY Zone (shaded band + ▲). Open=Low arms SHORT. When the BUY↔SHORT gap narrows below the ATR threshold, a 'pre-blast' zone is shaded (a big move is coming). Levels are non-repainting. Toggle via the BOLev_D / BOLev_W buttons.">
              {(() => {
                const bo = (draft as any).bolev || {};
                const setBo = (patchObj: any) => setDraft(d => ({ ...d, bolev: { ...(d as any).bolev, ...patchObj } } as any));
                // Per-side display toggles (default ON). bolevD / bolevW each get
                // independent Arrows / Lines / Narrow-zone switches.
                const toggleRow = (prefix: string, title: string) => (
                  <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, width: 52 }}>{title}</Typography>
                    <FormControlLabel control={<Switch size="small" checked={bo[`${prefix}ShowArrows`] !== false} onChange={e => setBo({ [`${prefix}ShowArrows`]: e.target.checked })} />} label={<Typography variant="caption">Crossover arrows</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={bo[`${prefix}ShowLines`] !== false} onChange={e => setBo({ [`${prefix}ShowLines`]: e.target.checked })} />} label={<Typography variant="caption">Lines</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={bo[`${prefix}ShowZone`] !== false} onChange={e => setBo({ [`${prefix}ShowZone`]: e.target.checked })} />} label={<Typography variant="caption">Narrow / pre-blast zone</Typography>} />
                  </Stack>
                );
                return (
                  <Stack spacing={1.2}>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="BUY level" value={bo.buyColor || '#26a69a'} onChange={v => setBo({ buyColor: v })} />
                      <ColorField label="SHORT level" value={bo.shortColor || '#ef5350'} onChange={v => setBo({ shortColor: v })} />
                      <Box sx={{ width: 200 }}>
                        <OpacitySlider label="Zone band opacity" value={bo.bandOpacity ?? 0.12} onChange={v => setBo({ bandOpacity: v })} />
                      </Box>
                      <LineWidthSlider label="Line width" value={bo.lineWidth ?? 2}
                        onChange={n => setBo({ lineWidth: n })} />
                      <PeriodSlider label="Lookback candles" value={bo.lookback ?? 2} min={1} max={10}
                        onChange={n => setBo({ lookback: n })} />
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>Display toggles</Typography>
                    {toggleRow('d', 'BO_D')}
                    {toggleRow('w', 'BO_W')}
                    {toggleRow('h4', 'BO_4H')}
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 0.5 }}>Channel fill (between BUY &amp; SHORT)</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <FormControlLabel control={<Switch size="small" checked={bo.channelFill === true} onChange={e => setBo({ channelFill: e.target.checked })} />} label={<Typography variant="caption">Fill channel</Typography>} />
                      <ColorField label="Channel" value={bo.channelColor || '#42a5f5'} onChange={v => setBo({ channelColor: v })} />
                      <Box sx={{ width: 200 }}>
                        <OpacitySlider label="Channel opacity" value={bo.channelOpacity ?? 0.08} onChange={v => setBo({ channelOpacity: v })} />
                      </Box>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 0.5 }}>Narrow / pre-blast zone</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <TextField size="small" type="number" label="ATR multiple" value={bo.narrowAtrMult ?? 1}
                        onChange={ev => { const v = Math.max(0.1, Math.min(10, Number(ev.target.value))); setBo({ narrowAtrMult: Number.isFinite(v) ? v : 1 }); }}
                        inputProps={{ min: 0.1, max: 10, step: 0.1 }} sx={{ width: 120 }}
                        helperText="gap < mult × ATR(14)" />
                      <ColorField label="Zone fill" value={bo.narrowColor || '#ffb74d'} onChange={v => setBo({ narrowColor: v })} />
                      <Box sx={{ width: 200 }}>
                        <OpacitySlider label="Zone fill opacity" value={bo.narrowOpacity ?? 0.18} onChange={v => setBo({ narrowOpacity: v })} />
                      </Box>
                    </Stack>
                  </Stack>
                );
              })()}
            </Section>

            <Section title="FBO — False Breakout (signal arrows)"
              subtitle="Lookback period for highest-high / lowest-low detection. Other parameters use the source pinescript's defaults.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <PeriodSlider label="Lookback period" value={draft.fbo.period} min={2} max={100}
                  onChange={n => patch('fbo', { period: n })} />
              </Stack>
            </Section>

            <Section title="BBR — oscillator"
              subtitle="BBR100 is %B of the HLC3 Bollinger band rescaled so the LOWER band reads −100, the basis 0 and the UPPER band +100 — so ±100 is the band itself, not a clamp, and readings run past it (measured −133..+148 across 60 symbols). Length and multiplier feed BOTH this curve and the BBR candle tint, so the two can never disagree. Switch the curve on with the BBR chip under Oscillators.">
              {paneToggle('bbr')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <PeriodSlider label="Bollinger length" value={draft.bbr.bbLen ?? 20} min={5} max={100}
                  onChange={n => patch('bbr', { bbLen: n })} />
                <PeriodSlider label="Std-dev multiplier" value={draft.bbr.bbMult ?? 2} min={1} max={4} step={0.1}
                  onChange={n => patch('bbr', { bbMult: n })} />
                <PeriodSlider label="Overbought level" value={draft.bbr.obLevel ?? 100} min={20} max={200}
                  onChange={n => patch('bbr', { obLevel: n })} />
                <PeriodSlider label="Oversold level" value={draft.bbr.osLevel ?? -100} min={-200} max={-20}
                  onChange={n => patch('bbr', { osLevel: n })} />
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ColorField label="Line" value={draft.bbr.lineColor || '#5e35b1'}
                  onChange={v => patch('bbr', { lineColor: v })} />
                <LineWidthSlider label="Line width" value={draft.bbr.lineWidth ?? 2}
                  onChange={n => patch('bbr', { lineWidth: n })} />
                <FormControl size="small" sx={{ width: 190 }}>
                  <InputLabel>Line style</InputLabel>
                  <Select label="Line style" value={draft.bbr.lineMode || 'step'}
                    onChange={e => patch('bbr', { lineMode: e.target.value as 'step' | 'smooth' })}>
                    <MenuItem value="step">Stepline</MenuItem>
                    <MenuItem value="smooth">Smooth</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {htfControls('bbr', draft.bbr, '#8e24aa', 'BBR')}
            </Section>

            <Section title="Wave — WaveTrend (oscillator + signal arrows)"
              subtitle="Channel and average lengths drive BOTH the ▲▼ arrows and the oscillator curve. SCALE NOTE: wt1 runs about −69..+71 and never reaches −100, so its ±60 levels are equivalent to BBR’s ±100 in RARITY (each tags ~5.8% of bars) but not in value. When both curves are on, the chart pins their two price scales so −60 and −100 land on the same row — comparable on screen, while the crosshair still shows each one’s true number.">
              {paneToggle('wave')}
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <PeriodSlider label="Channel length" value={draft.wave.channelLength} min={2} max={50}
                  onChange={n => patch('wave', { channelLength: n })} />
                <PeriodSlider label="Average length" value={draft.wave.averageLength} min={2} max={50}
                  onChange={n => patch('wave', { averageLength: n })} />
                <PeriodSlider label="Overbought level" value={draft.wave.obLevel ?? 60} min={20} max={100}
                  onChange={n => patch('wave', { obLevel: n })} />
                <PeriodSlider label="Oversold level" value={draft.wave.osLevel ?? -60} min={-100} max={-20}
                  onChange={n => patch('wave', { osLevel: n })} />
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ColorField label="wt1 line" value={draft.wave.lineColor || '#00897b'}
                  onChange={v => patch('wave', { lineColor: v })} />
                <LineWidthSlider label="Line width" value={draft.wave.lineWidth ?? 2}
                  onChange={n => patch('wave', { lineWidth: n })} />
                <FormControl size="small" sx={{ width: 190 }}>
                  <InputLabel>Line style</InputLabel>
                  <Select label="Line style" value={draft.wave.lineMode || 'step'}
                    onChange={e => patch('wave', { lineMode: e.target.value as 'step' | 'smooth' })}>
                    <MenuItem value="step">Stepline</MenuItem>
                    <MenuItem value="smooth">Smooth</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch size="small" checked={draft.wave.showWt2 === true}
                    onChange={ev => patch('wave', { showWt2: ev.target.checked })} />}
                  label={<Typography variant="caption">Show wt2 (4-bar SMA of wt1)</Typography>}
                />
                <ColorField label="wt2 line" value={draft.wave.wt2Color || '#80cbc4'}
                  onChange={v => patch('wave', { wt2Color: v })} />
              </Stack>
              {htfControls('wave', draft.wave, '#f9a825', 'Wave')}
            </Section>

            <Section title="BBR × Wave — the conjunction signal"
              subtitle="Marks BW▲ when BOTH oscillators sit at or below their oversold level AND BBR is rising against the previous bar. Measured over 171 Delta symbols / ~102k daily bars: +2.50% over the next 5 days against a −0.34% baseline (58.2% win vs 44.0%), firing on 156 of 171 symbols, and it held out of sample. Deliberately NOT the BBR-crosses-Wave rule it resembles — that measured +1.21%, a cross anywhere below zero measured −1.17%, and BBR oversold on its own is worth +0.01%. The conjunction is the whole effect. Needs the BBR or Wave oscillator chip on.">
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                Source candles — what BBR and Wave compute on
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Independent of the chart's candle type <em>by design</em>: switch the chart to HA to read the trend and
                these stay put. Governs the BBR curve, the Wave curve, the BW/BD/X marks and the BBR candle tint, so they
                always agree. Prices never come from here &mdash; the stop and the return are always measured on real bars.
                <br />
                <strong>A smoother source does not mean fewer signals.</strong> Measured over 128 crypto symbols and 29
                tickers &times; 10 years: every alternative fires <em>more</em> often, not less &mdash; Regular 3.36
                trades/symbol, CLH 3.88, CLN 5.28. BBR is a position <em>inside</em> a Bollinger band, so smoothing the
                source narrows the band too and the oscillator saturates sooner. What the smoothing really buys is a
                smaller curve: mean step 21.2 &rarr; 16.2 (CLH) &rarr; 12.7 (CLN). Only CLN also turns around less often
                (14.8 vs 41.2 direction changes per 100 bars); CLH actually turns slightly <em>more</em> (42.4).
                <br />
                Returns, crypto | equity: <strong>Regular +3.60% / 60.0% win | +3.25% / 70.8%</strong> &nbsp;·&nbsp;
                HA +2.80% / 60.5% | +3.30% / 71.3% &nbsp;·&nbsp; CLH +3.05% / <strong>49.1%</strong> | +4.58% / 62.1%
                &nbsp;·&nbsp; CLN +1.38% / 58.0% | +2.71% / 66.1%. Regular is the default because it is what the rule was
                measured on and it still returns most on crypto; CLH is the best of them on equities but costs 12pp of
                win rate there and 11pp on crypto.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Source candles</InputLabel>
                  <Select
                    value={(draft as any).bbrWave?.srcCandle || 'Regular'}
                    label="Source candles"
                    onChange={ev => patch('bbrWave', { srcCandle: ev.target.value } as any)}
                  >
                    <MenuItem value="Regular">Reg — real market OHLC (measured)</MenuItem>
                    <MenuItem value="HA">HA — Heikin-Ashi</MenuItem>
                    <MenuItem value="CLH">CLH — Custom Linreg HA</MenuItem>
                    <MenuItem value="CLN">CLN — regression body, wickless</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.showSignals !== false}
                    onChange={ev => patch('bbrWave', { showSignals: ev.target.checked })} />}
                  label={<Typography variant="caption">Mark the signal</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.requireTurn !== false}
                    onChange={ev => patch('bbrWave', { requireTurn: ev.target.checked })} />}
                  label={<Typography variant="caption">Require BBR turning up <em>(the trigger; off = both-oversold alone, which measured +2.34 vs +2.84)</em></Typography>}
                />
              </Stack>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ColorField label="Buy mark" value={draft.bbrWave?.buyColor || '#00c853'}
                  onChange={v => patch('bbrWave', { buyColor: v })} />
                <ColorField label="Short mark" value={draft.bbrWave?.shortColor || '#d50000'}
                  onChange={v => patch('bbrWave', { shortColor: v })} />
                <ColorField label="Guide lines" value={draft.bbrWave?.guideColor || '#90a4ae'}
                  onChange={v => patch('bbrWave', { guideColor: v })} />
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.showZones !== false}
                    onChange={ev => patch('bbrWave', { showZones: ev.target.checked })} />}
                  label={<Typography variant="caption">Shade oversold / overbought zones</Typography>}
                />
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                <strong>Curve colours.</strong> Both curves can take their colour from their OWN SLOPE —
                rising bullish, falling bearish. The direction is the thing this pane is for: the measured
                entry is “both oversold <em>and BBR rising</em>”, so the turn is the signal, and one flat
                hue hides it until the arrow prints. A flat bar keeps the previous direction, so a hold
                between two falls does not become a third colour. Switch it off to go back to the single
                colours set under BBR and Wave.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.slopeColor !== false}
                    onChange={ev => patch('bbrWave', { slopeColor: ev.target.checked })} />}
                  label={<Typography variant="caption">Colour BBR + Wave by their slope</Typography>}
                />
                <ColorField label="Rising" value={draft.bbrWave?.riseColor || '#26a69a'}
                  onChange={v => patch('bbrWave', { riseColor: v })} />
                <ColorField label="Falling" value={draft.bbrWave?.fallColor || '#ef5350'}
                  onChange={v => patch('bbrWave', { fallColor: v })} />
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.showMidline !== false}
                    onChange={ev => patch('bbrWave', { showMidline: ev.target.checked })} />}
                  label={<Typography variant="caption">Zero line <em>(the level the exit fires at)</em></Typography>}
                />
                <ColorField label="Zero line" value={draft.bbrWave?.midlineColor || 'rgba(120,130,140,0.38)'}
                  onChange={v => patch('bbrWave', { midlineColor: v })} />
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                The two zones have their <em>own</em> colours &mdash; reddish above, greenish below. They used to borrow the
                BBR <em>candle tint</em> colours, so restyling the tint silently restyled these bands, and setting both tint
                colours to the same hue made the two zones identical. Defaults follow the theme: the dark chart lifts them to
                brighter hues, because 10% of a deep red over <code>#0D1117</code> is very nearly nothing. Pick a colour here
                and it is used exactly as picked, on both themes.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ColorField label="Overbought zone" value={draft.bbrWave?.obZoneColor || '#c62828'}
                  onChange={v => patch('bbrWave', { obZoneColor: v })} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Overbought opacity"
                    value={draft.bbrWave?.obZoneOpacity ?? draft.bbrWave?.zoneOpacity ?? 0.10}
                    onChange={v => patch('bbrWave', { obZoneOpacity: v })} />
                </Box>
                <ColorField label="Oversold zone" value={draft.bbrWave?.osZoneColor || '#2e7d32'}
                  onChange={v => patch('bbrWave', { osZoneColor: v })} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Oversold opacity"
                    value={draft.bbrWave?.osZoneOpacity ?? draft.bbrWave?.zoneOpacity ?? 0.10}
                    onChange={v => patch('bbrWave', { osZoneOpacity: v })} />
                </Box>
              </Stack>

              <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5, fontWeight: 600 }}>
                Background tint — which curve is on top
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Tints the <em>whole</em> pane green while BBR is above Wave and red while it is at or below. Off by default:
                the pane already shades its overbought and oversold bands, so a second background is opt-in rather than
                something to discover and switch off. It needs both curves switched on &mdash; a tint taken from a line that
                is not drawn cannot be read. The comparison is made on the <em>pinned</em> scales, dividing each curve by its
                own overbought level, because BBR spans roughly &plusmn;148 against a threshold of 100 while Wave spans
                &plusmn;71 against 60: a raw &ldquo;BBR &gt; Wave&rdquo; would disagree with what you can see most of the
                time. Its opacity starts lower than the zone bands because it covers the entire pane.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.showRelZones === true}
                    onChange={e => patch('bbrWave', { showRelZones: e.target.checked })} />}
                  label={<Typography variant="caption">Tint the pane by BBR vs Wave</Typography>}
                />
                <ColorField label="BBR above Wave" value={draft.bbrWave?.relAboveColor || '#2e7d32'}
                  onChange={v => patch('bbrWave', { relAboveColor: v })} />
                <ColorField label="BBR below Wave" value={draft.bbrWave?.relBelowColor || '#c62828'}
                  onChange={v => patch('bbrWave', { relBelowColor: v })} />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Tint opacity"
                    value={draft.bbrWave?.relZoneOpacity ?? 0.08}
                    onChange={v => patch('bbrWave', { relZoneOpacity: v })} />
                </Box>
              </Stack>

              <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5, fontWeight: 600 }}>
                Higher-timeframe curves
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Switched on per oscillator in the BBR and Wave cards. The <em>forming</em> period is the one still open
                (today, on a 1H chart mapped to 1D): it is dashed and moves with every tick. The <em>confluence</em> tint
                shades the bars where every higher-timeframe curve that is on sits beyond its own oversold (green) or
                overbought (red) level &mdash; the bigger picture agreeing that this is an extreme. It uses the zone colours
                at its own, deliberately light opacity, since it covers the whole pane. It is a reading aid, not a tested
                signal, and no mark depends on it.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.htfShowForming !== false}
                    onChange={e => patch('bbrWave', { htfShowForming: e.target.checked })} />}
                  label={<Typography variant="caption">Draw the forming higher-timeframe period (dashed)</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.htfConfluence === true}
                    onChange={e => patch('bbrWave', { htfConfluence: e.target.checked })} />}
                  label={<Typography variant="caption">Tint where the higher timeframe is also oversold / overbought</Typography>}
                />
                <Box sx={{ width: 180 }}>
                  <OpacitySlider label="Confluence opacity"
                    value={draft.bbrWave?.htfConfluenceOpacity ?? 0.08}
                    onChange={v => patch('bbrWave', { htfConfluenceOpacity: v })} />
                </Box>
              </Stack>

              <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5, fontWeight: 600 }}>
                Exit — every entry gets a paired X
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Closing the BW▲ long when BBR returns to the midline, measured through <em>this</em> rule at the
                2.5&times;ATR default: <strong>+3.60% avg / 60.0% win</strong> on crypto, <strong>+3.25% / 70.8%</strong>{' '}
                on 10 years of equities. A flat 5-bar hold gives +2.20% / +2.39%.
                <br />
                <strong>The stop costs more than it saves.</strong> At 1.5&times;ATR it cut crypto to +2.81% / 52.4% and
                equities to +3.62% / 60.1% by stopping out trades that went on to recover. Set it to 0 and the figures are
                +3.90% / 60.3% and +4.39% / 77.3% &mdash; the best of any setting. Keeping a stop is a risk choice, not a
                returns one, so the size is yours.
                <br />
                Waiting for overbought instead, at this same stop, is +1.03% on crypto (far worse) and +3.36% on equities
                (slightly better); with no stop on either side it is +1.46% and +5.39%, so on equities it wins outright. The
                midline is the default because it is the one that is good in both, and because crypto is the traded market.
                Blue X = closed at the level, amber X = stopped out, grey X = ran out of bars.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1, mb: 1 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={draft.bbrWave?.showExits !== false}
                    onChange={ev => patch('bbrWave', { showExits: ev.target.checked })} />}
                  label={<Typography variant="caption">Mark exits</Typography>}
                />
                <PeriodSlider label="Exit at BBR level" value={draft.bbrWave?.exitLevel ?? 0} min={-100} max={100}
                  onChange={n => patch('bbrWave', { exitLevel: n })} />
                <PeriodSlider label="Stop (x ATR, 0 = none)" value={draft.bbrWave?.stopAtr ?? 1.5} min={0} max={5} step={0.1}
                  onChange={n => patch('bbrWave', { stopAtr: n })} />
                <PeriodSlider label="Max hold (bars)" value={draft.bbrWave?.maxHold ?? 20} min={2} max={100}
                  onChange={n => patch('bbrWave', { maxHold: n })} />
                <ColorField label="Exit mark" value={draft.bbrWave?.exitColor || '#1565c0'}
                  onChange={v => patch('bbrWave', { exitColor: v })} />
                <ColorField label="Stopped-out mark" value={draft.bbrWave?.stopColor || '#ef6c00'}
                  onChange={v => patch('bbrWave', { stopColor: v })} />
                <ColorField label="Timed-out mark" value={draft.bbrWave?.timeColor || '#607d8b'}
                  onChange={v => patch('bbrWave', { timeColor: v })} />
              </Stack>

              <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 0.5, fontWeight: 600 }}>
                Short entry — read this before switching it on
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                There is <strong>no mirror of the long rule</strong>. Both-overbought + BBR turning down was measured dead:
                −0.41 edge over 10 years of equities, 95% CI [−1.15, +0.29], negative in both halves. It has been removed
                rather than left switchable.
                <br />
                <strong>Breakdown</strong> (BBR crossing below 0, Wave below 0, price below its EMA50) is the best of seven
                designs I tested — and it is still mostly just short exposure. Crypto by year: 2024 −0.43,
                2025 <strong>+1.50</strong>, 2026 −0.50; the whole average is one year. Equities: 4 positive years
                against 6 negative. Marked <strong>BD▼</strong>, and like everything else here it is a chart mark
                — nothing on this page places an order.
              </Typography>
              <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <FormControl size="small" sx={{ width: 230 }}>
                  <InputLabel>Short entry</InputLabel>
                  <Select label="Short entry" value={draft.bbrWave?.shortMode || 'off'}
                    onChange={e => patch('bbrWave', { shortMode: e.target.value as 'off' | 'breakdown' })}>
                    <MenuItem value="off">Off (recommended)</MenuItem>
                    <MenuItem value="breakdown">Momentum breakdown</MenuItem>
                  </Select>
                </FormControl>
                <PeriodSlider label="Target (x ATR)" value={draft.bbrWave?.shortTargetAtr ?? 1.5} min={0.5} max={5} step={0.1}
                  onChange={n => patch('bbrWave', { shortTargetAtr: n })} />
                <PeriodSlider label="Stop (x ATR)" value={draft.bbrWave?.shortStopAtr ?? 1.5} min={0.5} max={5} step={0.1}
                  onChange={n => patch('bbrWave', { shortStopAtr: n })} />
                <PeriodSlider label="Max bars" value={draft.bbrWave?.shortMaxBars ?? 5} min={1} max={30}
                  onChange={n => patch('bbrWave', { shortMaxBars: n })} />
                <ColorField label="Short mark" value={draft.bbrWave?.shortColor || '#d50000'}
                  onChange={v => patch('bbrWave', { shortColor: v })} />
              </Stack>
            </Section>

            <Section title="LRC — POC breakout (★ / ★★)"
              subtitle="Marks when the candle CLOSE crosses the C-POC top/bottom (★ single star) or the H-POC top/bottom (★★ double star): cross above → BUY, cross below → SHORT. C-POC/H-POC are the same bands the C-POC/H-POC indicators draw. Pick which candle type's close to use below.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Candle type (close)</InputLabel>
                  <Select
                    label="Candle type (close)"
                    value={(draft as any).lrc?.candleType || 'Regular'}
                    onChange={ev => patch('lrc' as any, { candleType: ev.target.value } as any)}
                  >
                    {['Regular', 'HA', 'CSH', 'CSL', 'CLH'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Section>

            <Section title="Blast — Inside / Engulfing breakout"
              subtitle="Detects Inside candles (smoothed HA: high ≤ prev high AND low ≥ prev low) and Engulfing candles (Regular: engulfs the prior range and closes beyond its body) on DAILY & WEEKLY, irrespective of the chart's candle type / interval. Each detection draws a band at the candle's High/Low projected 2 periods forward (Daily band on ≤1D charts, Weekly on ≤1W). While a band is visible, a candle that freshly closes above the C-POC band → BUY ▲, below → SHORT ▼ (BL). Toggle via the 'Blast' chip after AS.">
              {(() => {
                const bl = draft.blast;
                return (
                  <Stack spacing={1.2}>
                    <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5 }}>
                      <FormControlLabel control={<Switch size="small" checked={bl.showDaily !== false} onChange={e => patch('blast', { showDaily: e.target.checked })} />} label={<Typography variant="caption">Show Daily staircase</Typography>} />
                      <FormControlLabel control={<Switch size="small" checked={bl.showWeekly !== false} onChange={e => patch('blast', { showWeekly: e.target.checked })} />} label={<Typography variant="caption">Show Weekly staircase</Typography>} />
                      <FormControlLabel control={<Switch size="small" checked={(bl as any).showSignals !== false} onChange={e => patch('blast', { showSignals: e.target.checked } as any)} />} label={<Typography variant="caption">Show BUY/SHORT signals</Typography>} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="Bullish (steps up)"   value={(bl as any).bullColor || '#26a69a'} onChange={v => patch('blast', { bullColor: v } as any)} />
                      <ColorField label="Bearish (steps down)" value={(bl as any).bearColor || '#ef5350'} onChange={v => patch('blast', { bearColor: v } as any)} />
                      <Box sx={{ width: 200 }}>
                        <OpacitySlider label="Daily opacity" value={bl.dailyOpacity} onChange={v => patch('blast', { dailyOpacity: v })} />
                      </Box>
                      <Box sx={{ width: 200 }}>
                        <OpacitySlider label="Weekly opacity" value={bl.weeklyOpacity} onChange={v => patch('blast', { weeklyOpacity: v })} />
                      </Box>
                    </Stack>
                  </Stack>
                );
              })()}
            </Section>

            <Section title="VOB — Volume-Trend Order Block"
              subtitle="Custom supertrend cloud (hl2 ± multiplier · SMA(high−low)) with a trailing-stop line; on confirmed swing pivots (Pivot Strength bars each side) and in the matching trend it draws volume-split order blocks — top 'Sell %' / bottom 'Buy %' by the buy/sell volume ratio. A new block can't overlap the active one; the active block extends right and (optionally) deletes when price fully breaks it. Retest crosses fire when price crosses back above a bull OB top / below a bear OB bottom, gated by a min Buy/Sell %. Computed on closed raw candles (repaint-safe), any candle type. Toggle via the 'VOB' chip after AV_Band.">
              {(() => {
                const vb = draft.vob;
                const numField = (label: string, value: number, on: (n: number) => void, opts: { min: number; max: number; step?: number; width?: number; help?: string }) => (
                  <TextField size="small" type="number" label={label} value={value}
                    onChange={ev => { const x = Number(ev.target.value); if (Number.isFinite(x)) on(Math.max(opts.min, Math.min(opts.max, x))); }}
                    inputProps={{ min: opts.min, max: opts.max, step: opts.step ?? 1 }} sx={{ width: opts.width ?? 130 }}
                    helperText={opts.help} />
                );
                return (
                  <Stack spacing={1.4}>
                    {/* Supertrend */}
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Supertrend</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <PeriodSlider label="Volatility SMA length" value={vb.stLen} min={5} max={200}
                        onChange={n => patch('vob', { stLen: n })} />
                      {numField('Multiplier', vb.stMult, n => patch('vob', { stMult: n }), { min: 0.1, max: 20, step: 0.1, width: 120 })}
                      <FormControlLabel control={<Switch size="small" checked={vb.showFill !== false} onChange={e => patch('vob', { showFill: e.target.checked })} />} label={<Typography variant="caption">Show trend cloud</Typography>} />
                      <ColorField label="Bull cloud" value={vb.cloudBullColor} onChange={v => patch('vob', { cloudBullColor: v })} />
                      <ColorField label="Bear cloud" value={vb.cloudBearColor} onChange={v => patch('vob', { cloudBearColor: v })} />
                    </Stack>
                    {/* Order Blocks */}
                    <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5 }}>Order Blocks</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <PeriodSlider label="Pivot strength" value={vb.pivotLen} min={1} max={50}
                        onChange={n => patch('vob', { pivotLen: n })} />
                      <ColorField label="Bullish OB" value={vb.bullColor} onChange={v => patch('vob', { bullColor: v })} />
                      <ColorField label="Bearish OB" value={vb.bearColor} onChange={v => patch('vob', { bearColor: v })} />
                      <ColorField label="Label text" value={vb.txtColor} onChange={v => patch('vob', { txtColor: v })} />
                      <FormControlLabel control={<Switch size="small" checked={vb.deleteOnBreak !== false} onChange={e => patch('vob', { deleteOnBreak: e.target.checked })} />} label={<Typography variant="caption">Delete on complete break</Typography>} />
                    </Stack>
                    {/* Retest filters */}
                    <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5 }}>Retest signals</Typography>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <FormControlLabel control={<Switch size="small" checked={vb.showBullRetest !== false} onChange={e => patch('vob', { showBullRetest: e.target.checked })} />} label={<Typography variant="caption">Bullish</Typography>} />
                      {numField('Min Buy %', vb.bullVolPct, n => patch('vob', { bullVolPct: n }), { min: 0, max: 100, step: 5, width: 110 })}
                      <ColorField label="Bull signal" value={vb.bullSigColor} onChange={v => patch('vob', { bullSigColor: v })} />
                      <FormControlLabel control={<Switch size="small" checked={vb.showBearRetest !== false} onChange={e => patch('vob', { showBearRetest: e.target.checked })} />} label={<Typography variant="caption">Bearish</Typography>} />
                      {numField('Min Sell %', vb.bearVolPct, n => patch('vob', { bearVolPct: n }), { min: 0, max: 100, step: 5, width: 110 })}
                      <ColorField label="Bear signal" value={vb.bearSigColor} onChange={v => patch('vob', { bearSigColor: v })} />
                    </Stack>
                  </Stack>
                );
              })()}
            </Section>

            <Section title="FNO — the options market on the chart"
              subtitle="One option expiry at a time, drawn candle by candle as it stood at each candle's close. Pick which lines and panes you want below. The expiry is switched from the FNO chip on the chart legend (or the D0 / W0 / M0 chips in the toolbar, or the expiry pills in the Options Workspace): D0 / W0 / M0 follow the front daily / weekly / monthly contract and roll to the next one the moment it settles; a date pins that one contract. A candle with no FNO record within 20 minutes is left empty (the lines break, the gap is shaded) — nothing is carried across an outage. Toggle the whole indicator with the 'FNO' chip.">
              {(() => {
                const fn = (draft as any).fno || {};
                const setFn = (patchObj: any) => patch('fno' as any, patchObj as any);
                const tier: 'D' | 'W' | 'M' = fn.tierM && !(fn.tierD !== false) && !(fn.tierW !== false) ? 'M' : (fn.tierW !== false) && !(fn.tierD !== false) ? 'W' : 'D';
                const sw = (key: string, label: string, def: boolean, tip?: string) => (
                  <Tooltip key={key} arrow title={tip || ''} disableHoverListener={!tip}>
                    <FormControlLabel
                      control={<Switch size="small" checked={def ? fn[key] !== false : fn[key] === true} onChange={e => setFn({ [key]: e.target.checked })} />}
                      label={<Typography variant="caption">{label}</Typography>} />
                  </Tooltip>
                );
                const head = (t: string) => <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5 }}>{t}</Typography>;
                const row = (children: React.ReactNode) => (
                  <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5 }}>{children}</Stack>
                );
                return (
                  <Stack spacing={1.2}>
                    {head('Default expiry (rolling)')}
                    {row(
                      <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Rolling tier</InputLabel>
                        <Select label="Rolling tier" value={tier} onChange={e => { const t = e.target.value as string; setFn({ tierD: t === 'D', tierW: t === 'W', tierM: t === 'M' }); }}>
                          <MenuItem value="D">D0 — front expiry (any kind)</MenuItem>
                          <MenuItem value="W">W0 — front weekly (incl. the monthly)</MenuItem>
                          <MenuItem value="M">M0 — front monthly</MenuItem>
                        </Select>
                      </FormControl>,
                    )}

                    {head('On the price chart')}
                    {row(<>
                      {sw('showSR', 'Call / put walls', true, 'The strike with the most call open interest (red, the writers’ ceiling) and the most put open interest (green, their floor), per candle.')}
                      {sw('showMaxPain', 'Max pain', true, 'Settlement price at which option buyers collectively lose the most (amber, dashed).')}
                      {sw('showGexFlip', 'γ flip', true, 'Price where dealer gamma changes sign (purple, long dashes). Above it dealers damp moves, below it they amplify them.')}
                      {sw('showOiRange', 'Wall range fill', true, 'A light fill between the call wall and the put wall — the range option writers are defending.')}
                      {sw('showExpMove', 'Expected move', false, 'Spot ± the ATM straddle premium: the move to expiry the options market is pricing.')}
                      {sw('showWalls', 'GEX walls', false, 'Strikes with the largest positive (green) and negative (red) dealer gamma.')}
                      {sw('showRegime', 'γ-regime tint', false, 'Tints the background: teal = dealers long gamma (ranges tend to hold), orange = short gamma (moves tend to run).')}
                      {sw('showRolls', 'Expiry roll marks', true, 'Marks the candle where D0 / W0 / M0 moved to the next contract.')}
                      {sw('showGaps', 'Shade data gaps', true, 'Shades candles where the FNO collector recorded nothing.')}
                    </>)}
                    {row(<>
                      <PeriodSlider label="Walls per side" value={fn.srCount ?? 1} min={1} max={4} onChange={n => setFn({ srCount: n })} />
                      <PeriodSlider label="GEX walls per side" value={fn.wallCount ?? 2} min={0} max={6} onChange={n => setFn({ wallCount: n })} />
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Lines</InputLabel>
                        <Select label="Lines" value={fn.lineMode === 'latest' ? 'latest' : 'historical'} onChange={e => setFn({ lineMode: e.target.value })}>
                          <MenuItem value="historical">Per candle (history)</MenuItem>
                          <MenuItem value="latest">Latest only (flat lines)</MenuItem>
                        </Select>
                      </FormControl>
                    </>)}

                    {head('Panes below the price')}
                    {row(<>
                      {sw('showPcr', 'PCR', true, 'Put/call open-interest ratio against 1.0: green above (more puts written — support below), red under (more calls — a cap above).')}
                      {sw('showPcrEma', 'PCR EMA', true)}
                      {sw('showIv', 'ATM IV', true, 'At-the-money implied volatility of the selected expiry.')}
                      {sw('showIvEma', 'IV EMA', false)}
                      {sw('showOi', 'OI change', true, 'Open interest added per candle: green bars = more puts than calls added (support building), red = more calls (resistance building).')}
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel>OI pane</InputLabel>
                        <Select label="OI pane" value={fn.oiMode === 'total' ? 'total' : 'net'} onChange={e => setFn({ oiMode: e.target.value })}>
                          <MenuItem value="net">Net change per candle</MenuItem>
                          <MenuItem value="total">Total calls vs puts</MenuItem>
                        </Select>
                      </FormControl>
                    </>)}
                    {row(<>
                      <PeriodSlider label="PCR EMA length" value={fn.pcrEmaLength ?? 7} min={2} max={50} onChange={n => setFn({ pcrEmaLength: n })} />
                      <PeriodSlider label="IV EMA length" value={fn.ivEmaLength ?? 7} min={2} max={50} onChange={n => setFn({ ivEmaLength: n })} />
                      <PeriodSlider label="History (days)" value={fn.days ?? 30} min={1} max={90} onChange={n => setFn({ days: n })} />
                    </>)}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Charts under 30 minutes use 5-minute FNO records and show at most the last 10 days; 30 minutes and up use 30-minute records.
                    </Typography>

                    {head('Option Profile (right rail, shares the price axis)')}
                    {row(<>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Profile</InputLabel>
                        <Select label="Profile" value={fn.profile || 'off'} onChange={e => setFn({ profile: e.target.value })}>
                          <MenuItem value="off">Off</MenuItem>
                          <MenuItem value="Combined">Combined (OI + ΔOI + GEX)</MenuItem>
                          <MenuItem value="GEX">GEX (γ walls)</MenuItem>
                          <MenuItem value="OI">OI (CE/PE walls)</MenuItem>
                          <MenuItem value="OIChg">ΔOI (build/unwind)</MenuItem>
                        </Select>
                      </FormControl>
                      <PeriodSlider label="Rail width (px)" value={fn.profileWidth ?? 110} min={60} max={220} onChange={n => setFn({ profileWidth: n })} />
                    </>)}

                    {head('Colours')}
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <ColorField label="Put wall / support" value={fn.srSupportColor || '#089981'} onChange={v => setFn({ srSupportColor: v })} />
                      <ColorField label="Call wall / resistance" value={fn.srResistanceColor || '#f23645'} onChange={v => setFn({ srResistanceColor: v })} />
                      <ColorField label="Max pain" value={fn.maxPainColor || '#ffb300'} onChange={v => setFn({ maxPainColor: v })} />
                      <ColorField label="γ flip" value={fn.gexFlipColor || '#ab47bc'} onChange={v => setFn({ gexFlipColor: v })} />
                      <ColorField label="Wall range fill" value={fn.oiRangeColor || '#5c6bc0'} onChange={v => setFn({ oiRangeColor: v })} />
                      <ColorField label="Expected move" value={fn.expMoveColor || '#90a4ae'} onChange={v => setFn({ expMoveColor: v })} />
                      <ColorField label="+γ wall" value={fn.wallPosColor || '#26a69a'} onChange={v => setFn({ wallPosColor: v })} />
                      <ColorField label="−γ wall" value={fn.wallNegColor || '#ef5350'} onChange={v => setFn({ wallNegColor: v })} />
                      <ColorField label="PCR EMA" value={fn.pcrEmaColor || '#ff9800'} onChange={v => setFn({ pcrEmaColor: v })} />
                      <ColorField label="IV" value={fn.ivColor || '#26c6da'} onChange={v => setFn({ ivColor: v })} />
                      <ColorField label="IV EMA" value={fn.ivEmaColor || '#ec407a'} onChange={v => setFn({ ivEmaColor: v })} />
                      <ColorField label="Basket TP line" value={fn.payoffTpColor || '#26a69a'} onChange={v => setFn({ payoffTpColor: v })} />
                      <ColorField label="Basket SL line" value={fn.payoffSlColor || '#ef5350'} onChange={v => setFn({ payoffSlColor: v })} />
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      The PCR and OI panes use the put-wall green and call-wall red, so one colour means the same thing everywhere.
                    </Typography>
                  </Stack>
                );
              })()}
            </Section>

            <Section title="VPA — Volume Spread Analysis (signal markers)"
              subtitle="Pick which VSA signals to display when the VPA indicator is on (the 'VPA' chip after BO_W). All off by default. Hover any signal to see its EXACT firing condition. Markers are computed on closed bars (repaint-safe) and stay visible on any candle type.">
              {(() => {
                const vpa: VpaToggles = { ...VPA_TOGGLE_DEFAULTS, ...(draft.vpa || {}) };
                const setKey = (k: keyof VpaToggles, v: boolean) => patch('vpa', { [k]: v } as Partial<VpaToggles>);
                const setMany = (keys: Array<keyof VpaToggles>, v: boolean) => {
                  const partial: Partial<VpaToggles> = {};
                  for (const k of keys) partial[k] = v;
                  patch('vpa', partial);
                };
                const groups: Array<{ group: VpaGroup; title: string; color: string }> = [
                  { group: 'bull', title: 'Bullish', color: '#26a69a' },
                  { group: 'bear', title: 'Bearish', color: '#ef5350' },
                ];
                return (
                  <Box>
                    <Box sx={{ mb: 1.25, p: 1, borderRadius: 1, bgcolor: 'action.hover',
                      whiteSpace: 'pre-line', fontSize: '0.66rem', lineHeight: 1.5, color: 'text.secondary' }}>
                      {[
                        'Terms used in the per-signal conditions (hover a toggle):',
                        '• Spread = high − low.  Avg spread / Avg volume = 40-bar SMA.',
                        '• Wide-range bar: spread > 1.5×avg.  Narrow-range: spread < 0.7×avg.',
                        '• Up-close: close in top 30% of the bar; Down-close: bottom 30%; Above/Below-close: above/below the 50% mid; Mid-close: 30–70% band.',
                        '• ClosePos 1→5 = bottom-20% → top-20%.  volpos 1→5 = very-high (>2×avg) → low.',
                        '• Up-bar / Down-bar: close >/< previous close.  Low-volume: V < prev AND V < 2-bars-ago AND V < avg.  High-volume: V > prev > 2-bars-ago.',
                        '• Trend gates (RWI): upmid = short-term up(1)/flat(0); upmajor = long-term up(+1)/down(−1)/flat(0); upminor = long-term up(+1)/down(−1).',
                      ].join('\n')}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
                      <Button size="small" variant="outlined" onClick={() => setMany(VPA_SIGNAL_DEFS.map(d => d.key), true)}>All on</Button>
                      <Button size="small" variant="outlined" onClick={() => setMany(VPA_SIGNAL_DEFS.map(d => d.key), false)}>All off</Button>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2.5, rowGap: 1.5 }}>
                      {groups.map(g => {
                        const defs = VPA_SIGNAL_DEFS.filter(d => d.group === g.group);
                        const keys = defs.map(d => d.key);
                        const allOn = keys.every(k => !!vpa[k]);
                        return (
                          <Box key={g.group} sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              px: 1.25, py: 0.5, bgcolor: `${g.color}1f`, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: g.color }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: g.color, letterSpacing: 0.3 }}>{g.title}</Typography>
                              </Box>
                              <Button size="small" variant="text" sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '0.62rem', lineHeight: 1.6 }}
                                onClick={() => setMany(keys, !allOn)}>{allOn ? 'Clear' : 'All'}</Button>
                            </Box>
                            <Stack sx={{ px: 1, py: 0.5 }}>
                              {defs.map(d => (
                                <Tooltip key={d.key} title={d.tip} arrow placement="left">
                                  <FormControlLabel
                                    sx={{ ml: 0, mr: 0, height: 30, justifyContent: 'flex-start',
                                      '& .MuiFormControlLabel-label': { fontSize: '0.72rem', display: 'flex', alignItems: 'baseline', gap: 0.5 } }}
                                    control={<Switch size="small" checked={!!vpa[d.key]} onChange={ev => setKey(d.key, ev.target.checked)} sx={{ mr: 0.75 }} />}
                                    label={<>
                                      <Box component="span" sx={{ fontWeight: 700, minWidth: 34, display: 'inline-block' }}>{d.code}</Box>
                                      <Box component="span" sx={{ opacity: 0.75 }}>{d.label}</Box>
                                    </>}
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Section>

            <Section title="HLDiff — High/Low EMA"
              subtitle="EMA of the Highs and EMA of the Lows of the selected candle type. One EMA period applies to both. Computed from raw candles + the chosen transform — independent of the chart's display candle type. Line mode overlays both EMA lines (with a translucent fill) on the price chart; Histogram mode plots the High−Low EMA spread in the lower pane, coloured by the slope of the two EMAs.">
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Display mode</InputLabel>
                  <Select
                    value={(draft as any).hlDiff?.mode === 'line' ? 'line' : 'histogram'}
                    label="Display mode"
                    onChange={ev => patch('hlDiff' as any, { mode: ev.target.value } as any)}
                  >
                    <MenuItem value="histogram">Histogram (lower pane)</MenuItem>
                    <MenuItem value="line">Line (on price chart)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Candle type</InputLabel>
                  <Select
                    value={(draft as any).hlDiff?.candleType || 'HA'}
                    label="Candle type"
                    onChange={ev => patch('hlDiff' as any, { candleType: ev.target.value } as any)}
                  >
                    <MenuItem value="Regular">Regular</MenuItem>
                    <MenuItem value="HA">HA — Heikin-Ashi</MenuItem>
                    <MenuItem value="CSH">CSH — Custom Smooth HA</MenuItem>
                    <MenuItem value="CSL">CSL — Custom Smooth Linreg</MenuItem>
                    <MenuItem value="CLH">CLH — Custom Linreg HA</MenuItem>
                  </Select>
                </FormControl>
                <PeriodSlider label="EMA period (Highs & Lows)" value={(draft as any).hlDiff?.emaPeriod ?? 21} min={2} max={200}
                  onChange={n => patch('hlDiff' as any, { emaPeriod: n } as any)} />
              </Stack>
              {((draft as any).hlDiff?.mode === 'line') ? (
                <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1.5, mt: 1 }}>
                  <PeriodSlider label="Line width" value={(draft as any).hlDiff?.lineWidth ?? 1} min={1} max={4}
                    onChange={n => patch('hlDiff' as any, { lineWidth: n } as any)} />
                  <ColorField label="High EMA line" value={(draft as any).hlDiff?.highColor || '#26a69a'}
                    onChange={v => patch('hlDiff' as any, { highColor: v } as any)} />
                  <ColorField label="Low EMA line" value={(draft as any).hlDiff?.lowColor || '#ef5350'}
                    onChange={v => patch('hlDiff' as any, { lowColor: v } as any)} />
                  <ColorField label="Fill colour" value={(draft as any).hlDiff?.fillColor || '#90a4ae'}
                    onChange={v => patch('hlDiff' as any, { fillColor: v } as any)} />
                  <Box sx={{ width: 220 }}>
                    <OpacitySlider label="Fill opacity" value={(draft as any).hlDiff?.fillOpacity ?? 0.15}
                      onChange={v => patch('hlDiff' as any, { fillOpacity: v } as any)} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1.5, mt: 1 }}>
                  <ColorField label="Both EMAs rising" value={(draft as any).hlDiff?.histBothRiseColor || '#26a69a'}
                    onChange={v => patch('hlDiff' as any, { histBothRiseColor: v } as any)} />
                  <ColorField label="Both EMAs falling" value={(draft as any).hlDiff?.histBothFallColor || '#ef5350'}
                    onChange={v => patch('hlDiff' as any, { histBothFallColor: v } as any)} />
                  <ColorField label="High ↑ / Low ↓" value={(draft as any).hlDiff?.histHiRiseLoFallColor || '#ef9a9a'}
                    onChange={v => patch('hlDiff' as any, { histHiRiseLoFallColor: v } as any)} />
                  <ColorField label="High ↓ / Low ↑" value={(draft as any).hlDiff?.histHiFallLoRiseColor || '#a5d6a7'}
                    onChange={v => patch('hlDiff' as any, { histHiFallLoRiseColor: v } as any)} />
                  <Box sx={{ width: 220 }}>
                    <OpacitySlider label="Bar opacity" value={(draft as any).hlDiff?.histOpacity ?? 0.8}
                      onChange={v => patch('hlDiff' as any, { histOpacity: v } as any)} />
                  </Box>
                </Stack>
              )}
            </Section>

            <Section title="S/R — Support / Resistance"
              subtitle="Horizontal S/R levels from one or more source methods, all drawn on the price chart at once (more methods coming). The newest level per stream is 'active' (deep); older ones stay faint. HTF lines are thicker than current-TF.">
              {/* Method 1 — HLDiff SR */}
              <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                <FormControlLabel
                  control={<Switch size="small" checked={(draft as any).sr?.methods?.hlDiff?.enabled !== false}
                    onChange={ev => patch('sr' as any, { methods: { ...(draft as any).sr?.methods, hlDiff: { ...(draft as any).sr?.methods?.hlDiff, enabled: ev.target.checked } } } as any)} />}
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>HLDiff SR</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Levels at HLDiff-histogram peaks (uses the candle type + EMA period from HLDiff above). Dark-green peak → Resistance at the bar's high; dark-red peak → Support at the bar's low.
                </Typography>
                {((draft as any).sr?.methods?.hlDiff?.enabled !== false) && (
                  <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                    <FormControlLabel
                      control={<Switch size="small" checked={(draft as any).sr?.methods?.hlDiff?.useCurrentTf !== false}
                        onChange={ev => patch('sr' as any, { methods: { ...(draft as any).sr?.methods, hlDiff: { ...(draft as any).sr?.methods?.hlDiff, useCurrentTf: ev.target.checked } } } as any)} />}
                      label={<Typography variant="caption">Current TF</Typography>}
                    />
                    <FormControlLabel
                      control={<Switch size="small" checked={(draft as any).sr?.methods?.hlDiff?.useHtf !== false}
                        onChange={ev => patch('sr' as any, { methods: { ...(draft as any).sr?.methods, hlDiff: { ...(draft as any).sr?.methods?.hlDiff, useHtf: ev.target.checked } } } as any)} />}
                      label={<Typography variant="caption">HTF (Strategies mapping)</Typography>}
                    />
                    <PeriodSlider label="Max levels / stream" value={(draft as any).sr?.methods?.hlDiff?.maxLevelsPerTf ?? 3} min={1} max={10}
                      onChange={n => patch('sr' as any, { methods: { ...(draft as any).sr?.methods, hlDiff: { ...(draft as any).sr?.methods?.hlDiff, maxLevelsPerTf: n } } } as any)} />
                  </Stack>
                )}
              </Box>
              {/* Shared styling */}
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1.5, mt: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Display</InputLabel>
                  <Select
                    value={(draft as any).sr?.displayMode === 'band' ? 'band' : 'levels'}
                    label="Display"
                    onChange={ev => patch('sr' as any, { displayMode: ev.target.value } as any)}
                  >
                    <MenuItem value="levels">Levels (horizontal lines)</MenuItem>
                    <MenuItem value="band">Running band (channel)</MenuItem>
                  </Select>
                </FormControl>
                <ColorField label="Resistance" value={(draft as any).sr?.resistanceColor || '#ef5350'}
                  onChange={v => patch('sr' as any, { resistanceColor: v } as any)} />
                <ColorField label="Support" value={(draft as any).sr?.supportColor || '#26a69a'}
                  onChange={v => patch('sr' as any, { supportColor: v } as any)} />
                <PeriodSlider label="Current-TF width" value={(draft as any).sr?.ctfWidth ?? 1} min={1} max={5}
                  onChange={n => patch('sr' as any, { ctfWidth: n } as any)} />
                <PeriodSlider label="HTF width" value={(draft as any).sr?.htfWidth ?? 2} min={1} max={5}
                  onChange={n => patch('sr' as any, { htfWidth: n } as any)} />
              </Stack>
              {((draft as any).sr?.displayMode === 'band') && (
                <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1.5, mt: 1 }}>
                  <ColorField label="Band fill" value={(draft as any).sr?.bandFillColor || '#90a4ae'}
                    onChange={v => patch('sr' as any, { bandFillColor: v } as any)} />
                  <Box sx={{ width: 200 }}>
                    <OpacitySlider label="Band fill opacity" value={(draft as any).sr?.bandFillOpacity ?? 0.12}
                      onChange={v => patch('sr' as any, { bandFillOpacity: v } as any)} />
                  </Box>
                </Stack>
              )}
              <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1.5, mt: 1 }}>
                <Box sx={{ width: 200 }}>
                  <OpacitySlider label="Active opacity" value={(draft as any).sr?.activeOpacity ?? 0.95}
                    onChange={v => patch('sr' as any, { activeOpacity: v } as any)} />
                </Box>
                <Box sx={{ width: 200 }}>
                  <OpacitySlider label="Inactive opacity" value={(draft as any).sr?.inactiveOpacity ?? 0.3}
                    onChange={v => patch('sr' as any, { inactiveOpacity: v } as any)} />
                </Box>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Line style</InputLabel>
                  <Select
                    value={(draft as any).sr?.lineStyle ?? 2}
                    label="Line style"
                    onChange={ev => patch('sr' as any, { lineStyle: Number(ev.target.value) } as any)}
                  >
                    <MenuItem value={0}>Solid</MenuItem>
                    <MenuItem value={1}>Dotted</MenuItem>
                    <MenuItem value={2}>Dashed</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch size="small" checked={(draft as any).sr?.showLabels !== false}
                    onChange={ev => patch('sr' as any, { showLabels: ev.target.checked } as any)} />}
                  label={<Typography variant="caption">Show labels</Typography>}
                />
              </Stack>

              {/* Break / Respect markers */}
              <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                <FormControlLabel
                  control={<Switch size="small" checked={!!(draft as any).sr?.markers?.enabled}
                    onChange={ev => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, enabled: ev.target.checked } } as any)} />}
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Break / Respect markers (HTF)</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Plots CLH-body vs HTF-S/R events: confirmed breakout/breakdown (Brk↑/Brk↓), false-breach traps (Trap) and respect bounce/rejection (Bnc/Rej). Needs the HLDiff SR method with HTF on. Detection is on closed bars and look-ahead-safe.
                </Typography>
                {!!(draft as any).sr?.markers?.enabled && (
                  <>
                    <Stack direction="row" useFlexGap spacing={3} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                      <Box sx={{ width: 210 }}>
                        <OpacitySlider label="Buffer (× channel width)" value={(draft as any).sr?.markers?.bufFrac ?? 0.12}
                          onChange={v => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, bufFrac: v } } as any)} />
                      </Box>
                      <PeriodSlider label="Confirm window (bars)" value={(draft as any).sr?.markers?.window ?? 3} min={1} max={10}
                        onChange={n => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, window: n } } as any)} />
                      <PeriodSlider label="Hold closes" value={(draft as any).sr?.markers?.holds ?? 2} min={1} max={5}
                        onChange={n => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, holds: n } } as any)} />
                      <PeriodSlider label="Cooldown (bars)" value={(draft as any).sr?.markers?.cooldown ?? 2} min={0} max={10}
                        onChange={n => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, cooldown: n } } as any)} />
                      <PeriodSlider label="Test lookback (bars)" value={(draft as any).sr?.markers?.testLookback ?? 3} min={1} max={10}
                        onChange={n => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, testLookback: n } } as any)} />
                    </Stack>
                    <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                      <FormControlLabel
                        control={<Switch size="small" checked={(draft as any).sr?.markers?.showConfirmed !== false}
                          onChange={ev => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, showConfirmed: ev.target.checked } } as any)} />}
                        label={<Typography variant="caption">Confirmed breaks</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch size="small" checked={(draft as any).sr?.markers?.showFalse !== false}
                          onChange={ev => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, showFalse: ev.target.checked } } as any)} />}
                        label={<Typography variant="caption">False breaches (traps)</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch size="small" checked={(draft as any).sr?.markers?.showRespected !== false}
                          onChange={ev => patch('sr' as any, { markers: { ...(draft as any).sr?.markers, showRespected: ev.target.checked } } as any)} />}
                        label={<Typography variant="caption">Respect bounce / rejection</Typography>}
                      />
                    </Stack>
                  </>
                )}
              </Box>
            </Section>

            <Section title="Order Flow ribbon (Delta only)"
              subtitle="Per-candle bias from per-minute taker buy/sell volume (3-day rolling window). 7-state colour ribbon under the candles; hover for the regime label.">
              <FormControlLabel
                control={<Switch checked={(draft as any).flow?.showStrip !== false}
                  onChange={ev => setDraft(d => ({ ...d, flow: { ...(d as any).flow, showStrip: ev.target.checked } } as any))} />}
                label={<Typography variant="caption">Show ribbon</Typography>}
              />
            </Section>
            </SortedSections>
          </Box>
        )}

        {/* ─────────────────── TAB 5 — GENERAL ─────────────────── */}
        {tab === 'general' && (
          <Box>
            <SortedSections>
            <Section title="Chart loading"
              subtitle="Opening any symbol now restores the indicators, timeframe and layout you last used on any symbol (remembered automatically). On the very first load, a default indicator set (OverView, Bkground, AvgCloud, C-POC, LR_HAMA_Band, RelStrength, ChgN%, MACD) is turned on."
              defaultExpanded>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                There's nothing to configure here — your last-used chart setup is applied to every symbol automatically.
              </Typography>
            </Section>
            </SortedSections>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Tooltip arrow title="Make YOUR current active chart template + app preferences (theme, order defaults, indicators, FNO/OI config) the live default that every NEW user is seeded with when they register. Account, risk, broker and Telegram settings are never shared.">
          <Button
            size="small" color="secondary" startIcon={<CheckCircleIcon />}
            onClick={async () => {
              if (!window.confirm('Set your current chart + app settings as the default for all NEW users? They will be seeded from your live setup at registration.')) return;
              try { await chartSettingsService.setNewUserDefault(); toast.success('Done — new users will now start with your current setup.', { duration: 6000 }); }
              catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to set default'); }
            }}
            sx={{ mr: 'auto' }}
          >
            Set as new-user default
          </Button>
        </Tooltip>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


export default ChartSettingsDialog;
